import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Building2, Users, FileText, Upload, Plus, Calendar, DollarSign,
  Search, Eye, Edit, Trash2, Check, X, Download, BookOpen,
  AlertCircle, Settings, ChevronDown, ChevronUp, Clock, UserPlus, Package, Wand2, Copy, FileEdit,
  TrendingUp, RefreshCw, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Genera identificador único VBX-AÑOMESDIAHORAMINUTOSEGUNDO
const generateVbxIsbn = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `VBX-${year}${month}${day}${hours}${minutes}${seconds}`;
};

const AdminRoyalties = ({ token }) => {
  const [activeSection, setActiveSection] = useState('reports'); // reports, authors, platforms, import, pending, history
  
  // Sales History state (Historial de Ventas)
  const [salesHistory, setSalesHistory] = useState([]);
  const [loadingSalesHistory, setLoadingSalesHistory] = useState(false);
  const [salesHistoryFilters, setSalesHistoryFilters] = useState({
    dateFrom: '',
    dateTo: '',
    platform: '',
    author_id: '',
    search: ''
  });
  const [salesHistorySummary, setSalesHistorySummary] = useState({
    totalSales: 0,
    totalUnits: 0,
    totalGrossIncome: 0,
    totalNetRoyalties: 0,
    byPlatform: {},
    byAuthor: {},
    byMonth: {}
  });
  
  // Authors state
  const [authors, setAuthors] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [loadingAuthors, setLoadingAuthors] = useState(true);
  const [authorSearch, setAuthorSearch] = useState('');
  
  // Pending Sales state
  const [pendingSales, setPendingSales] = useState([]);
  const [pendingSummary, setPendingSummary] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [selectedPendingIsbn, setSelectedPendingIsbn] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningIsbn, setAssigningIsbn] = useState(null);
  const [selectedAuthorForAssign, setSelectedAuthorForAssign] = useState('');
  const [assigning, setAssigning] = useState(false);
  
  // Reports state
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showCreateReport, setShowCreateReport] = useState(false);
  const [newReport, setNewReport] = useState({
    author_id: '',
    period_type: 'monthly',
    period_start: '',
    period_end: '',
    currency: 'MXN'
  });
  
  // Platforms state
  const [platforms, setPlatforms] = useState([]);
  const [loadingPlatforms, setLoadingPlatforms] = useState(true);
  const [newPlatform, setNewPlatform] = useState('');
  
  // Entry form state
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({
    isbn: '',
    platform: '',
    units_sold: 0,
    gross_income: 0,
    currency: 'MXN',
    exchange_rate: 1.0
  });
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importConfig, setImportConfig] = useState({
    period_type: 'monthly',
    period_start: '',
    period_end: '',
    currency: 'MXN',
    author_id: '',
    create_works: true
  });
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);
  
  // Duplicate detection state
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [forceUpload, setForceUpload] = useState(false);

  // Platform Profits (5BX y Viubux)
  const [platformProfits, setPlatformProfits] = useState(null);
  const [loadingProfits, setLoadingProfits] = useState(false);

  // Works state (Obras)
  const [works, setWorks] = useState([]);
  const [loadingWorks, setLoadingWorks] = useState(false);
  const [orphanCount, setOrphanCount] = useState(0);
  const [showOrphansOnly, setShowOrphansOnly] = useState(true);
  const [workSearch, setWorkSearch] = useState('');
  const [selectedWork, setSelectedWork] = useState(null);
  const [showAssignWorkModal, setShowAssignWorkModal] = useState(false);
  const [selectedAuthorForWork, setSelectedAuthorForWork] = useState('');
  const [assigningWork, setAssigningWork] = useState(false);
  const [showCreateWorkModal, setShowCreateWorkModal] = useState(false);
  const [isbnMode, setIsbnMode] = useState('auto'); // 'auto' or 'manual'
  const [newWork, setNewWork] = useState({
    title: '',
    isbn: '',
    author_id: '',
    formats: [],
    public_url: '',
    cover_image: '',
    content_type: 'audiobook',
    coproducers: []
  });
  const [newWorkCoproducer, setNewWorkCoproducer] = useState({
    user_id: '',
    percentage: '',
    role: 'coproductor'
  });
  
  // Helper to safely reset newWork state
  const resetNewWorkState = () => {
    setNewWork({
      title: '',
      isbn: '',
      author_id: '',
      formats: [],
      public_url: '',
      cover_image: '',
      content_type: 'audiobook',
      coproducers: []
    });
    setIsbnMode('auto');
    setNewWorkCoproducer({ user_id: '', percentage: '', role: 'coproductor' });
  };

  // Direct Sale states
  const [showDirectSaleModal, setShowDirectSaleModal] = useState(false);
  const [directSale, setDirectSale] = useState({
    author_id: '',
    work_id: '',
    format: '',
    description: '',
    platform: 'Venta Directa',
    units_sold: 1,
    gross_income: '',
    currency: 'MXN'
  });
  const [savingDirectSale, setSavingDirectSale] = useState(false);
  const [authorWorks, setAuthorWorks] = useState([]);
  const [loadingAuthorWorks, setLoadingAuthorWorks] = useState(false);

  // Pool Contribution states
  const [showPoolContributionModal, setShowPoolContributionModal] = useState(false);
  const [poolContribution, setPoolContribution] = useState({
    description: '',
    amount: '',
    currency: 'MXN'
  });
  const [savingPoolContribution, setSavingPoolContribution] = useState(false);
  const [poolContributions, setPoolContributions] = useState([]);

  // Manual Webhook Sale states (for missed sales from Publica.la)
  const [showWebhookSaleModal, setShowWebhookSaleModal] = useState(false);
  const [webhookSale, setWebhookSale] = useState({
    author_email: '',
    author_name: '',  // Nombre del autor para crear Author Lite si no existe
    isbn: '',
    content_name: '',
    content_type: 'audio',
    sale_amount_mxn: '',
    payout_amount_usd: '',
    platform: 'Viubux',
    publisher: '',
    sale_date: ''
  });
  const [savingWebhookSale, setSavingWebhookSale] = useState(false);
  const [autoCreatedAuthor, setAutoCreatedAuthor] = useState(null);  // Info del autor auto-creado

  // Coproducción states
  const [showCoproducersModal, setShowCoproducersModal] = useState(false);
  const [selectedWorkForCoprod, setSelectedWorkForCoprod] = useState(null);
  const [coproducers, setCoproducers] = useState([]);
  const [loadingCoproducers, setLoadingCoproducers] = useState(false);
  const [newCoproducer, setNewCoproducer] = useState({
    user_id: '',
    percentage: '',
    role: 'coproductor'
  });
  const [savingCoproducer, setSavingCoproducer] = useState(false);
  const [coproducerStats, setCoproducerStats] = useState({
    total_percentage_assigned: 0,
    percentage_available: 100
  });

  // Publica.la Sync states
  const [publicalaStatus, setPublicalaStatus] = useState(null);
  const [loadingPublicalaStatus, setLoadingPublicalaStatus] = useState(false);
  const [syncingPublicala, setSyncingPublicala] = useState(false);

  // Entry editing state
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);

  useEffect(() => {
    fetchAuthors();
    fetchPlatforms();
    fetchPlatformProfits(); // Load platform profits on init
  }, []);

  useEffect(() => {
    if (activeSection === 'pending') {
      fetchPendingSales();
    }
    if (activeSection === 'works') {
      fetchWorks();
    }
    if (activeSection === 'history') {
      fetchSalesHistory();
    }
    if (activeSection === 'import') {
      fetchPublicalaStatus();
    }
  }, [activeSection, showOrphansOnly]);

  useEffect(() => {
    if (selectedAuthor) {
      fetchReports(selectedAuthor.id);
    }
  }, [selectedAuthor]);

  // Fetch Sales History (Historial de Ventas)
  const fetchSalesHistory = async () => {
    setLoadingSalesHistory(true);
    try {
      const params = new URLSearchParams();
      if (salesHistoryFilters.dateFrom) params.append('date_from', salesHistoryFilters.dateFrom);
      if (salesHistoryFilters.dateTo) params.append('date_to', salesHistoryFilters.dateTo);
      if (salesHistoryFilters.platform) params.append('platform', salesHistoryFilters.platform);
      if (salesHistoryFilters.author_id) params.append('author_id', salesHistoryFilters.author_id);
      if (salesHistoryFilters.search) params.append('search', salesHistoryFilters.search);
      
      const response = await axios.get(
        `${API}/api/admin/sales-history?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSalesHistory(response.data.sales || []);
      setSalesHistorySummary(response.data.summary || {
        totalSales: 0,
        totalUnits: 0,
        totalGrossIncome: 0,
        totalNetRoyalties: 0,
        byPlatform: {},
        byAuthor: {},
        byMonth: {}
      });
    } catch (error) {
      console.error('Error fetching sales history:', error);
      toast.error('Error al cargar historial de ventas');
    } finally {
      setLoadingSalesHistory(false);
    }
  };

  // Download Sales History as CSV
  const downloadSalesHistoryCSV = () => {
    if (salesHistory.length === 0) {
      toast.error('No hay datos para descargar');
      return;
    }
    
    const headers = ['Fecha', 'ID Transacción', 'Obra', 'ISBN', 'Autor', 'Plataforma', 'Unidades', 'Ingreso Bruto', 'Moneda', 'Tipo Cambio', 'Regalía Neta', 'Periodo'];
    const rows = salesHistory.map(sale => [
      sale.created_at?.substring(0, 10) || '',
      sale.id || sale.transaction_id || '',
      sale.work_title || sale.content_name || '',
      sale.isbn || '',
      sale.author_name || '',
      sale.platform || '',
      sale.units_sold || 0,
      sale.gross_income || 0,
      sale.currency || 'MXN',
      sale.exchange_rate || 1,
      sale.net_royalty || 0,
      sale.period || ''
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `historial_ventas_5bx_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Historial descargado');
  };

  // ============== DUPLICATE DETECTION HELPERS ==============
  
  // Calculate SHA-256 hash of file content
  const calculateFileHash = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const buffer = e.target.result;
          const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          resolve(hashHex);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Check if file has been uploaded before
  const checkFileHashDuplicate = async (fileHash, filename) => {
    try {
      const response = await axios.post(
        `${API}/api/admin/royalties/check-file-hash`,
        { file_hash: fileHash, filename },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking file hash:', error);
      return { is_duplicate: false };
    }
  };

  // Register file upload after successful import
  const registerFileUpload = async (fileHash, filename, entriesCount) => {
    try {
      await axios.post(
        `${API}/api/admin/royalties/register-file-upload`,
        { file_hash: fileHash, filename, entries_count: entriesCount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error registering file upload:', error);
    }
  };

  // Pre-check for duplicates before importing
  const preCheckDuplicates = async () => {
    if (!importFile) {
      toast.error('Por favor selecciona un archivo CSV o Excel');
      return false;
    }
    if (!importConfig.period_start) {
      toast.error('Por favor configura la fecha de inicio del período');
      return false;
    }
    if (!importConfig.period_end) {
      toast.error('Por favor configura la fecha de fin del período');
      return false;
    }

    setCheckingDuplicates(true);
    setDuplicateWarning(null);

    try {
      // Step 1: Calculate file hash
      const fileHash = await calculateFileHash(importFile);
      
      // Step 2: Check if this exact file was uploaded before
      const fileHashResult = await checkFileHashDuplicate(fileHash, importFile.name);
      
      if (fileHashResult.is_duplicate) {
        setDuplicateWarning({
          type: 'file',
          fileHash,
          message: fileHashResult.message,
          originalUpload: fileHashResult.original_upload
        });
        setShowDuplicateModal(true);
        setCheckingDuplicates(false);
        return false;
      }

      // File is new, proceed with import
      setDuplicateWarning({ type: 'none', fileHash });
      return { proceed: true, fileHash };
    } catch (error) {
      console.error('Error during duplicate pre-check:', error);
      toast.error('Error al verificar duplicados');
      setCheckingDuplicates(false);
      return false;
    }
  };
  // ============== END DUPLICATE DETECTION HELPERS ==============

  // Delete sale from history
  const handleDeleteSale = async (sale) => {
    if (!window.confirm(`¿Eliminar esta venta?\n\nObra: ${sale.work_title || sale.content_name || 'Sin título'}\nMonto: $${sale.gross_income || 0}\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API}/api/admin/sales-history/${sale.id}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          data: { source: sale.source, report_id: sale.report_id }
        }
      );
      toast.success(response.data.message || 'Venta eliminada');
      fetchSalesHistory();
      fetchPlatformProfits(); // Refresh profits
    } catch (error) {
      console.error('Error deleting sale:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar venta');
    }
  };

  // Fetch Platform Profits (5BX 15% + Viubux 33%)
  const fetchPlatformProfits = async () => {
    setLoadingProfits(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/royalties/platform-profits`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlatformProfits(response.data);
    } catch (error) {
      console.error('Error fetching platform profits:', error);
    } finally {
      setLoadingProfits(false);
    }
  };

  const fetchPendingSales = async () => {
    setLoadingPending(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/pending-royalty-sales`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingSales(response.data.pending_sales || []);
      setPendingSummary(response.data.summary_by_isbn || []);
    } catch (error) {
      console.error('Error fetching pending sales:', error);
      toast.error('Error al cargar ventas pendientes');
    } finally {
      setLoadingPending(false);
    }
  };

  const handleAssignSales = async () => {
    if (!assigningIsbn || !selectedAuthorForAssign) {
      toast.error('Selecciona un autor para asignar');
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/assign-pending-sales`,
        { isbn: assigningIsbn, author_id: selectedAuthorForAssign },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.sales_assigned} ventas asignadas exitosamente`);
      if (response.data.reports_created > 0) {
        toast.info(`${response.data.reports_created} reportes creados`);
      }
      if (response.data.reports_updated > 0) {
        toast.info(`${response.data.reports_updated} reportes actualizados`);
      }
      
      setShowAssignModal(false);
      setAssigningIsbn(null);
      setSelectedAuthorForAssign('');
      fetchPendingSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar ventas');
    } finally {
      setAssigning(false);
    }
  };

  // ============== PUBLICA.LA SYNC FUNCTIONS ==============
  
  const fetchPublicalaStatus = async () => {
    setLoadingPublicalaStatus(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/publicala/sales-report-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPublicalaStatus(response.data);
    } catch (error) {
      console.error('Error fetching Publica.la status:', error);
    } finally {
      setLoadingPublicalaStatus(false);
    }
  };

  const handleSyncPublicala = async () => {
    setSyncingPublicala(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/publicala/sync-sales`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      if (data.synced > 0) {
        toast.success(`✅ ${data.synced} ventas sincronizadas desde Publica.la`);
      } else {
        toast.info('No hay nuevas ventas para sincronizar');
      }
      
      if (data.skipped > 0) {
        toast.info(`${data.skipped} ventas ya sincronizadas (omitidas)`);
      }
      
      // Refresh status
      fetchPublicalaStatus();
      fetchSalesHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSyncingPublicala(false);
    }
  };

  // ============== WORKS FUNCTIONS ==============
  const fetchWorks = async () => {
    setLoadingWorks(true);
    try {
      const params = new URLSearchParams();
      if (showOrphansOnly) params.append('orphans_only', 'true');
      params.append('limit', '100');
      
      const response = await axios.get(
        `${API}/api/admin/works?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWorks(response.data.works || []);
      setOrphanCount(response.data.orphan_count || 0);
    } catch (error) {
      console.error('Error fetching works:', error);
      toast.error('Error al cargar obras');
    } finally {
      setLoadingWorks(false);
    }
  };

  const handleAssignWork = async () => {
    if (!selectedWork || !selectedAuthorForWork) {
      toast.error('Selecciona un autor para asignar');
      return;
    }

    setAssigningWork(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/works/assign`,
        { work_id: selectedWork.id, author_id: selectedAuthorForWork },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowAssignWorkModal(false);
      setSelectedWork(null);
      setSelectedAuthorForWork('');
      fetchWorks();
      fetchAuthors(); // Refresh author works count
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar obra');
    } finally {
      setAssigningWork(false);
    }
  };

  // Delete work entirely
  const handleDeleteWork = async (work) => {
    const confirmMsg = `¿Estás seguro de eliminar esta obra?\n\nTítulo: ${work.title}\nISBN: ${work.isbn || 'Sin ISBN'}\nAutor: ${authors.find(a => a.id === work.author_id)?.full_name || 'Sin asignar'}\n\n⚠️ Esta acción no se puede deshacer.\nLas ventas asociadas se marcarán como huérfanas.`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API}/api/admin/works/${work.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      if (response.data.orphaned_pending_sales > 0 || response.data.orphaned_report_entries > 0) {
        toast.info(`${response.data.orphaned_pending_sales} ventas pendientes y ${response.data.orphaned_report_entries} reportes marcados como huérfanos`);
      }
      fetchWorks();
      fetchAuthors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar obra');
    }
  };

  const handleCreateWork = async () => {
    if (!newWork.title || !newWork.author_id) {
      toast.error('El título y el autor son requeridos');
      return;
    }

    // Validate coproducers total percentage
    const totalCoprodPercent = newWork.coproducers.reduce((sum, c) => sum + parseFloat(c.percentage || 0), 0);
    if (totalCoprodPercent >= 100) {
      toast.error('El porcentaje total de co-productores no puede ser 100% o más');
      return;
    }

    try {
      const workData = {
        ...newWork,
        has_coproduction: newWork.coproducers.length > 0
      };
      
      const response = await axios.post(
        `${API}/api/admin/works/create-and-assign`,
        workData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowCreateWorkModal(false);
      resetNewWorkState();
      fetchWorks();
      fetchAuthors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear obra');
    }
  };

  // Add coproducer to new work
  const addCoproducerToNewWork = () => {
    if (!newWorkCoproducer.user_id || !newWorkCoproducer.percentage) {
      toast.error('Selecciona un usuario y define el porcentaje');
      return;
    }
    
    const percentage = parseFloat(newWorkCoproducer.percentage);
    const currentTotal = newWork.coproducers.reduce((sum, c) => sum + parseFloat(c.percentage || 0), 0);
    
    if (percentage <= 0 || percentage > 99) {
      toast.error('El porcentaje debe ser entre 0.1 y 99%');
      return;
    }
    
    if (currentTotal + percentage >= 100) {
      toast.error(`Solo puedes asignar ${(99 - currentTotal).toFixed(1)}% más`);
      return;
    }
    
    // Check if user already added
    if (newWork.coproducers.some(c => c.user_id === newWorkCoproducer.user_id)) {
      toast.error('Este usuario ya está como co-productor');
      return;
    }
    
    // Get user name for display
    const selectedUser = authors.find(a => a.id === newWorkCoproducer.user_id);
    
    setNewWork({
      ...newWork,
      coproducers: [
        ...newWork.coproducers,
        {
          ...newWorkCoproducer,
          percentage: percentage,
          user_name: selectedUser?.full_name || 'Usuario'
        }
      ]
    });
    setNewWorkCoproducer({ user_id: '', percentage: '', role: 'coproductor' });
  };

  // Remove coproducer from new work
  const removeCoproducerFromNewWork = (userId) => {
    setNewWork({
      ...newWork,
      coproducers: newWork.coproducers.filter(c => c.user_id !== userId)
    });
  };

  const filteredWorks = works.filter(w => 
    w.title?.toLowerCase().includes(workSearch.toLowerCase()) ||
    w.isbn?.toLowerCase().includes(workSearch.toLowerCase())
  );

  // ============== COPRODUCCIÓN FUNCTIONS ==============
  const fetchCoproducers = async (workId) => {
    setLoadingCoproducers(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/works/${workId}/coproducers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCoproducers(response.data.coproducers || []);
      setCoproducerStats({
        total_percentage_assigned: response.data.total_percentage_assigned || 0,
        percentage_available: response.data.percentage_available || 100
      });
    } catch (error) {
      toast.error('Error al cargar coproductores');
      console.error(error);
    } finally {
      setLoadingCoproducers(false);
    }
  };

  const openCoproducersModal = (work) => {
    setSelectedWorkForCoprod(work);
    setShowCoproducersModal(true);
    fetchCoproducers(work.id);
    setNewCoproducer({ user_id: '', percentage: '', role: 'coproductor' });
  };

  const handleAddCoproducer = async () => {
    if (!newCoproducer.user_id || !newCoproducer.percentage) {
      toast.error('Selecciona un usuario y define el porcentaje');
      return;
    }

    const percentage = parseFloat(newCoproducer.percentage);
    if (percentage <= 0 || percentage > coproducerStats.percentage_available) {
      toast.error(`El porcentaje debe ser entre 0.1 y ${coproducerStats.percentage_available}%`);
      return;
    }

    setSavingCoproducer(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/works/${selectedWorkForCoprod.id}/coproducers`,
        {
          user_id: newCoproducer.user_id,
          percentage: percentage,
          role: newCoproducer.role
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setNewCoproducer({ user_id: '', percentage: '', role: 'coproductor' });
      fetchCoproducers(selectedWorkForCoprod.id);
      fetchWorks(); // Refresh works to update has_coproduction flag
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar coproductor');
    } finally {
      setSavingCoproducer(false);
    }
  };

  const handleRemoveCoproducer = async (userId) => {
    if (!confirm('¿Eliminar este coproductor?')) return;

    try {
      await axios.delete(
        `${API}/api/admin/works/${selectedWorkForCoprod.id}/coproducers/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Coproductor eliminado');
      fetchCoproducers(selectedWorkForCoprod.id);
      fetchWorks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleUpdateCoproducerPercentage = async (userId, newPercentage) => {
    try {
      await axios.put(
        `${API}/api/admin/works/${selectedWorkForCoprod.id}/coproducers/${userId}`,
        { percentage: parseFloat(newPercentage) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Porcentaje actualizado');
      setEditPct((prev) => { const n = { ...prev }; delete n[userId]; return n; });
      fetchCoproducers(selectedWorkForCoprod.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const [editPct, setEditPct] = useState({});

  // ============== DIRECT SALE FUNCTIONS ==============
  
  // Fetch works for selected author
  const fetchAuthorWorks = async (authorId) => {
    if (!authorId) {
      setAuthorWorks([]);
      return;
    }
    
    setLoadingAuthorWorks(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/works?author_id=${authorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAuthorWorks(response.data.works || []);
    } catch (error) {
      console.error('Error fetching author works:', error);
      setAuthorWorks([]);
    } finally {
      setLoadingAuthorWorks(false);
    }
  };

  // Handle author selection for direct sale
  const handleDirectSaleAuthorChange = (authorId) => {
    setDirectSale({
      ...directSale,
      author_id: authorId,
      work_id: '',
      format: ''
    });
    fetchAuthorWorks(authorId);
  };

  // Get formats for selected work
  const getWorkFormats = () => {
    if (!directSale.work_id) return [];
    const work = authorWorks.find(w => w.id === directSale.work_id);
    return work?.formats || [];
  };

  const handleAddDirectSale = async () => {
    if (!directSale.author_id || !directSale.gross_income) {
      toast.error('Completa todos los campos requeridos');
      return;
    }

    // If work is selected, require format
    if (directSale.work_id && !directSale.format) {
      toast.error('Selecciona el formato del libro');
      return;
    }

    setSavingDirectSale(true);
    try {
      // Build description from work if not provided
      let description = directSale.description;
      if (!description && directSale.work_id) {
        const work = authorWorks.find(w => w.id === directSale.work_id);
        const formatLabel = {
          'audiobook': 'Audiolibro',
          'ebook': 'eBook',
          'printed': 'Impreso',
          'videobook': 'Videolibro'
        }[directSale.format] || directSale.format;
        description = `${work?.title || 'Obra'} (${formatLabel})`;
      }

      const response = await axios.post(
        `${API}/api/admin/royalties/direct-sale/author`,
        {
          author_id: directSale.author_id,
          description: description || 'Venta Directa',
          work_id: directSale.work_id || null,
          format: directSale.format || null,
          platform: directSale.platform || 'Venta Directa',
          units_sold: parseInt(directSale.units_sold) || 1,
          gross_income: parseFloat(directSale.gross_income),
          currency: directSale.currency || 'MXN'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowDirectSaleModal(false);
      setDirectSale({
        author_id: '',
        work_id: '',
        format: '',
        description: '',
        platform: 'Venta Directa',
        units_sold: 1,
        gross_income: '',
        currency: 'MXN'
      });
      setAuthorWorks([]);
      fetchReports();
      fetchAuthors();
    } catch (error) {
      console.error('Error adding direct sale:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Error al agregar venta directa';
      toast.error(errorMsg);
    } finally {
      setSavingDirectSale(false);
    }
  };

  // Registrar venta de webhook manualmente (Publica.la)
  const handleAddWebhookSale = async () => {
    // ISBN, content_name y payout_amount_usd son requeridos
    // author_email es opcional (se crea Author Lite si no existe)
    if (!webhookSale.isbn || !webhookSale.content_name || !webhookSale.payout_amount_usd) {
      toast.error('Completa los campos requeridos: ISBN, Nombre del Contenido y Monto USD');
      return;
    }

    // Si no hay email del autor, debe haber un nombre para crear Author Lite
    if (!webhookSale.author_email && !webhookSale.author_name) {
      toast.error('Proporciona el email del autor existente O el nombre para crear un Author Lite');
      return;
    }

    setSavingWebhookSale(true);
    setAutoCreatedAuthor(null);
    
    try {
      const response = await axios.post(
        `${API}/api/admin/royalties/manual-webhook-sale`,
        {
          ...webhookSale,
          sale_amount_mxn: parseFloat(webhookSale.sale_amount_mxn) || 0,
          payout_amount_usd: parseFloat(webhookSale.payout_amount_usd)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Si se creó un Author Lite automáticamente, mostrar los detalles
      if (response.data.author_auto_created && response.data.new_author_details) {
        setAutoCreatedAuthor(response.data.new_author_details);
        toast.success(`✅ ${response.data.message}`, { duration: 8000 });
      } else {
        toast.success(response.data.message);
        setShowWebhookSaleModal(false);
      }
      
      setWebhookSale({
        author_email: '',
        author_name: '',
        isbn: '',
        content_name: '',
        content_type: 'audio',
        sale_amount_mxn: '',
        payout_amount_usd: '',
        platform: 'Viubux',
        publisher: '',
        sale_date: ''
      });
      fetchReports();
      fetchAuthors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar venta de webhook');
    } finally {
      setSavingWebhookSale(false);
    }
  };

  const handleAddPoolContribution = async () => {
    if (!poolContribution.description || !poolContribution.amount) {
      toast.error('Completa todos los campos requeridos');
      return;
    }

    setSavingPoolContribution(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/royalties/direct-sale/pool`,
        {
          ...poolContribution,
          amount: parseFloat(poolContribution.amount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowPoolContributionModal(false);
      setPoolContribution({
        description: '',
        amount: '',
        currency: 'MXN'
      });
      fetchPoolContributions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar aporte al pool');
    } finally {
      setSavingPoolContribution(false);
    }
  };

  const fetchPoolContributions = async () => {
    try {
      const response = await axios.get(
        `${API}/api/admin/royalties/pool-contributions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPoolContributions(response.data.contributions || []);
    } catch (error) {
      console.error('Error fetching pool contributions:', error);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm('¿Estás seguro de eliminar este reporte completo? Esta acción no se puede deshacer y eliminará todas las ventas asociadas.')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/api/admin/royalties/reports/${reportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Reporte eliminado correctamente');
      setSelectedReport(null);
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar reporte');
    }
  };

  const handleUpdateReportStatus = async (reportId, newStatus) => {
    try {
      await axios.put(
        `${API}/api/admin/royalties/reports/${reportId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Reporte marcado como ${newStatus === 'paid' ? 'Pagado' : newStatus === 'approved' ? 'Aprobado' : 'Pendiente'}`);
      
      // Refresh
      if (selectedReport) {
        const response = await axios.get(
          `${API}/api/admin/royalties/reports/${reportId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSelectedReport(response.data);
      }
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar reporte');
    }
  };

  const handleDeleteEntry = async (reportId, entryId) => {
    if (!confirm('¿Estás seguro de eliminar esta venta? Esta acción no se puede deshacer.')) {
      return;
    }

    setDeletingEntry(entryId);
    try {
      await axios.delete(
        `${API}/api/admin/royalties/reports/${reportId}/entries/${entryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Venta eliminada correctamente');
      
      // Refresh selected report
      if (selectedReport) {
        const response = await axios.get(
          `${API}/api/admin/royalties/reports/${reportId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSelectedReport(response.data);
      }
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar venta');
    } finally {
      setDeletingEntry(null);
    }
  };

  const fetchAuthors = async () => {
    try {
      const response = await axios.get(
        `${API}/api/admin/royalties/authors`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAuthors(response.data.authors || []);
    } catch (error) {
      console.error('Error fetching authors:', error);
    } finally {
      setLoadingAuthors(false);
    }
  };

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(
        `${API}/api/admin/royalties/platforms`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlatforms(response.data.platforms || []);
    } catch (error) {
      console.error('Error fetching platforms:', error);
    } finally {
      setLoadingPlatforms(false);
    }
  };

  const fetchReports = async (authorId) => {
    setLoadingReports(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/royalties/reports?author_id=${authorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleCreateReport = async () => {
    if (!newReport.author_id || !newReport.period_start || !newReport.period_end) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/api/admin/royalties/reports/create`,
        newReport,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReports([response.data.report, ...reports]);
      setShowCreateReport(false);
      setSelectedReport(response.data.report);
      toast.success('Reporte creado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear reporte');
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.isbn || !newEntry.platform || newEntry.units_sold <= 0) {
      toast.error('ISBN, plataforma y unidades son requeridos');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/api/admin/royalties/reports/${selectedReport.id}/add-entry`,
        newEntry,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setSelectedReport({
        ...selectedReport,
        entries: [...(selectedReport.entries || []), response.data.entry],
        total_units: response.data.totals.units,
        total_gross: response.data.totals.gross,
        total_net_royalty: response.data.totals.net
      });
      
      setShowAddEntry(false);
      setNewEntry({ isbn: '', platform: '', units_sold: 0, gross_income: 0, currency: 'MXN', exchange_rate: 1.0 });
      toast.success('Entrada agregada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar entrada');
    }
  };

  // handleDeleteEntry moved above (line 351)

  const handlePublishReport = async () => {
    try {
      await axios.post(
        `${API}/api/admin/royalties/reports/${selectedReport.id}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedReport({ ...selectedReport, status: 'published' });
      toast.success('Reporte publicado - El autor puede verlo ahora');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al publicar');
    }
  };

  const handleMarkPaid = async () => {
    try {
      await axios.post(
        `${API}/api/admin/royalties/reports/${selectedReport.id}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedReport({ ...selectedReport, status: 'paid' });
      toast.success('Reporte marcado como pagado');
    } catch (error) {
      toast.error('Error al marcar como pagado');
    }
  };

  const handleAddPlatform = async () => {
    if (!newPlatform.trim()) return;
    
    try {
      const response = await axios.post(
        `${API}/api/admin/royalties/platforms`,
        { name: newPlatform },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPlatforms([...platforms, response.data.platform]);
      setNewPlatform('');
      toast.success('Plataforma agregada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar plataforma');
    }
  };

  const handleDeletePlatform = async (platformId) => {
    try {
      await axios.delete(
        `${API}/api/admin/royalties/platforms/${platformId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPlatforms(platforms.filter(p => p.id !== platformId));
      toast.success('Plataforma eliminada');
    } catch (error) {
      toast.error('Error al eliminar plataforma');
    }
  };

  const handleImportCSV = async (forceImport = false) => {
    if (!importFile) {
      toast.error('Por favor selecciona un archivo CSV o Excel');
      return;
    }
    if (!importConfig.period_start) {
      toast.error('Por favor configura la fecha de inicio del período');
      return;
    }
    if (!importConfig.period_end) {
      toast.error('Por favor configura la fecha de fin del período');
      return;
    }

    // If not forcing import, do duplicate pre-check first
    if (!forceImport && !forceUpload) {
      setCheckingDuplicates(true);
      const preCheckResult = await preCheckDuplicates();
      setCheckingDuplicates(false);
      
      if (!preCheckResult || !preCheckResult.proceed) {
        // Either there was an error or duplicate was found (modal shown)
        return;
      }
      
      // Store file hash for later registration
      setDuplicateWarning({ type: 'none', fileHash: preCheckResult.fileHash });
    }

    setImporting(true);
    setImportErrors([]);
    const formData = new FormData();
    formData.append('file', importFile);
    formData.append('period_type', importConfig.period_type);
    formData.append('period_start', importConfig.period_start);
    formData.append('period_end', importConfig.period_end);
    formData.append('currency', importConfig.currency);
    formData.append('create_works', importConfig.create_works);
    if (importConfig.author_id) {
      formData.append('author_id', importConfig.author_id);
    }

    try {
      const response = await axios.post(
        `${API}/api/admin/royalties/import-csv`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      const data = response.data;
      
      // Guardar resumen para mostrar totales post-importación
      if (data.summary) setImportSummary({ ...data.summary, processed: data.processed_rows });
      
      // Register file hash after successful import
      if (duplicateWarning?.fileHash) {
        await registerFileUpload(duplicateWarning.fileHash, importFile.name, data.processed_rows || 0);
      }
      
      let successMsg = `Importación exitosa: ${data.processed_rows} ventas procesadas`;
      if (data.reports_created > 0) successMsg += `, ${data.reports_created} reportes creados`;
      if (data.reports_updated > 0) successMsg += `, ${data.reports_updated} reportes actualizados`;
      
      toast.success(successMsg);
      
      // Show duplicate skipped count
      if (data.skipped_duplicates > 0) {
        toast.warning(`⚠️ ${data.skipped_duplicates} transacciones duplicadas omitidas (ya existían en el sistema)`);
      }
      
      if (data.errors && data.errors.length > 0) {
        setImportErrors(data.errors);
        toast.warning(`${data.errors.length} filas con errores`);
      }
      
      if (data.works_created > 0) {
        toast.info(`${data.works_created} obras creadas automáticamente`);
      }
      
      if (data.platforms_created > 0) {
        toast.info(`${data.platforms_created} plataformas nuevas registradas`);
      }
      
      if (data.skipped_gifts > 0 || data.skipped_status > 0) {
        toast.info(`${data.skipped_gifts || 0} regalos y ${data.skipped_status || 0} no aprobados omitidos`);
      }
      
      // Reset state
      setImportFile(null);
      setDuplicateWarning(null);
      setForceUpload(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Refresh if author selected
      if (selectedAuthor) {
        fetchReports(selectedAuthor.id);
      }
      
      // Also refresh sales history and pending
      fetchSalesHistory();
      fetchPendingSales();
      
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al importar archivo';
      toast.error(detail);
      setImportErrors([detail]);
    } finally {
      setImporting(false);
      setForceUpload(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
  };

  // Helper to render formats (can be strings or objects with type/isbn)
  const renderFormats = (formats) => {
    if (!formats || !Array.isArray(formats) || formats.length === 0) return null;
    
    const formatLabels = {
      audiobook: '🎧 Audiolibro',
      ebook: '📖 eBook', 
      printed: '📚 Impreso',
      videobook: '📹 Videolibro'
    };
    
    return formats.map(f => {
      // If it's an object with type field, use the type
      const formatType = typeof f === 'object' ? (f.type || f.format || 'unknown') : f;
      return formatLabels[formatType] || formatType;
    }).join(', ');
  };

  const filteredAuthors = authors.filter(a => 
    a.full_name?.toLowerCase().includes(authorSearch.toLowerCase()) ||
    a.email?.toLowerCase().includes(authorSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="gotham-bold text-2xl text-white flex items-center gap-3">
            <Building2 className="w-7 h-7 text-green-400" />
            5BX PUBLISHING & DISTRIBUTION
          </h2>
          <p className="text-white/60 text-sm">Sistema de Regalías para Autores</p>
        </div>
      </div>

      {/* Platform Profits Summary (5BX 15% + Viubux 33%) */}
      {platformProfits && (
        <div className="bg-gradient-to-r from-purple-900/30 via-blue-900/30 to-emerald-900/30 rounded-xl p-5 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="gotham-bold text-lg text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Resumen de Ganancias por Plataforma
            </h3>
            <button 
              onClick={fetchPlatformProfits}
              disabled={loadingProfits}
              className="text-white/50 hover:text-white transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`w-4 h-4 ${loadingProfits ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Ingreso Bruto Total */}
            <div className="bg-black/30 rounded-lg p-4 border border-white/5">
              <p className="text-white/50 text-xs mb-1">Ingreso Bruto Total</p>
              <p className="text-white text-2xl font-bold">{formatCurrency(platformProfits.summary?.total_gross_income || 0)}</p>
              <p className="text-white/40 text-xs mt-1">Base de cálculo</p>
            </div>
            
            {/* Viubux (33%) */}
            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-lg p-4 border border-purple-500/30">
              <p className="text-purple-300 text-xs mb-1 flex items-center gap-1">
                <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                Viubux (33%)
              </p>
              <p className="text-purple-200 text-2xl font-bold">{formatCurrency(platformProfits.summary?.viubux_profit?.amount || 0)}</p>
              <p className="text-purple-400/70 text-xs mt-1">Ganancia plataforma</p>
            </div>
            
            {/* 5BX (15%) */}
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-lg p-4 border border-blue-500/30">
              <p className="text-blue-300 text-xs mb-1 flex items-center gap-1">
                <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                5BX Global Distribution (15%)
              </p>
              <p className="text-blue-200 text-2xl font-bold">{formatCurrency(platformProfits.summary?.bx5_commission?.amount || 0)}</p>
              <p className="text-blue-400/70 text-xs mt-1">Comisión distribución</p>
            </div>
            
            {/* Autores (~52%) */}
            <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-lg p-4 border border-green-500/30">
              <p className="text-green-300 text-xs mb-1 flex items-center gap-1">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                Regalías Autores (~52%)
              </p>
              <p className="text-green-200 text-2xl font-bold">{formatCurrency(platformProfits.summary?.author_royalties?.amount || 0)}</p>
              <p className="text-green-400/70 text-xs mt-1">Total para autores</p>
            </div>
          </div>
          
          {/* Works Breakdown */}
          {platformProfits.works_breakdown?.length > 0 && (
            <div className="mt-4">
              <p className="text-white/70 text-sm mb-2">Desglose por Obra (Top {platformProfits.works_breakdown.length})</p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {platformProfits.works_breakdown.slice(0, 10).map((work, idx) => (
                  <div key={work.work_id || idx} className="bg-black/20 rounded p-3 flex items-center justify-between text-sm">
                    <div className="flex-1">
                      <p className="text-white font-medium truncate">{work.title}</p>
                      <p className="text-white/50 text-xs">{work.author_name} • {work.units_sold} unidades</p>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <div className="text-right">
                        <p className="text-white/50">Bruto</p>
                        <p className="text-white font-medium">{formatCurrency(work.gross_income)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-purple-400/70">Viubux</p>
                        <p className="text-purple-300">{formatCurrency(work.viubux_profit)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-blue-400/70">5BX</p>
                        <p className="text-blue-300">{formatCurrency(work.bx5_commission)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400/70">Autor</p>
                        <p className="text-green-300">{formatCurrency(work.author_royalties)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => setActiveSection('reports')}
          className={activeSection === 'reports' ? 'btn-viubux' : 'bg-white/10 text-white hover:bg-white/20'}
          size="sm"
        >
          <FileText className="w-4 h-4 mr-2" />
          Reportes
        </Button>
        <Button
          onClick={() => setActiveSection('import')}
          className={activeSection === 'import' ? 'btn-viubux' : 'bg-white/10 text-white hover:bg-white/20'}
          size="sm"
        >
          <Upload className="w-4 h-4 mr-2" />
          Importar CSV
        </Button>
        <Button
          onClick={() => setActiveSection('pending')}
          className={activeSection === 'pending' ? 'btn-viubux' : 'bg-white/10 text-white hover:bg-white/20'}
          size="sm"
          data-testid="pending-sales-tab"
        >
          <Clock className="w-4 h-4 mr-2" />
          Ventas Pendientes
          {pendingSummary.length > 0 && (
            <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
              {pendingSummary.length}
            </span>
          )}
        </Button>
        <Button
          onClick={() => setActiveSection('platforms')}
          className={activeSection === 'platforms' ? 'btn-viubux' : 'bg-white/10 text-white hover:bg-white/20'}
          size="sm"
        >
          <Settings className="w-4 h-4 mr-2" />
          Plataformas
        </Button>
        <Button
          onClick={() => setActiveSection('works')}
          className={activeSection === 'works' ? 'btn-viubux' : 'bg-white/10 text-white hover:bg-white/20'}
          size="sm"
          data-testid="works-tab"
        >
          <BookOpen className="w-4 h-4 mr-2" />
          Obras
          {orphanCount > 0 && (
            <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {orphanCount}
            </span>
          )}
        </Button>
        <Button
          onClick={() => setActiveSection('history')}
          className={activeSection === 'history' ? 'btn-viubux bg-emerald-600' : 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30'}
          size="sm"
          data-testid="history-tab"
        >
          <Calendar className="w-4 h-4 mr-2" />
          📊 Historial General
        </Button>
      </div>

      {/* Reports Section */}
      {activeSection === 'reports' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Authors List */}
          <Card className="card-viubux-dark border-purple-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Autores
              </CardTitle>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  value={authorSearch}
                  onChange={(e) => setAuthorSearch(e.target.value)}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg pl-10 pr-4 py-2 text-white text-sm"
                  placeholder="Buscar autor..."
                />
              </div>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              {loadingAuthors ? (
                <div className="text-center py-4">
                  <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredAuthors.map(author => (
                    <div
                      key={author.id}
                      onClick={() => {
                        setSelectedAuthor(author);
                        setSelectedReport(null);
                      }}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedAuthor?.id === author.id
                          ? 'bg-purple-500/30 border border-purple-500'
                          : 'bg-black/30 border border-transparent hover:border-purple-500/50'
                      }`}
                    >
                      <p className="text-white font-medium truncate">{author.full_name}</p>
                      <p className="text-white/50 text-xs truncate">{author.email}</p>
                      <p className="text-purple-400 text-xs mt-1">
                        <BookOpen className="w-3 h-3 inline mr-1" />
                        {author.works_count || 0} obras
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reports List */}
          <Card className="card-viubux-dark border-purple-500/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  Reportes
                </CardTitle>
                {selectedAuthor && (
                  <Button
                    onClick={() => {
                      setNewReport({ ...newReport, author_id: selectedAuthor.id });
                      setShowCreateReport(true);
                    }}
                    size="sm"
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              {!selectedAuthor ? (
                <p className="text-white/40 text-center py-8">Selecciona un autor</p>
              ) : loadingReports ? (
                <div className="text-center py-4">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : reports.length === 0 ? (
                <p className="text-white/40 text-center py-8">Sin reportes</p>
              ) : (
                <div className="space-y-2">
                  {reports.map(report => (
                    <div
                      key={report.id}
                      className={`p-3 rounded-lg transition-colors ${
                        selectedReport?.id === report.id
                          ? 'bg-blue-500/30 border border-blue-500'
                          : 'bg-black/30 border border-transparent hover:border-blue-500/50'
                      }`}
                    >
                      <div 
                        onClick={() => setSelectedReport(report)}
                        className="cursor-pointer"
                      >
                        <p className="text-white text-sm">{report.period_start} - {report.period_end}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            report.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            report.status === 'published' || report.status === 'approved' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {report.status === 'paid' ? 'Pagado' : report.status === 'published' || report.status === 'approved' ? 'Aprobado' : 'Pendiente'}
                          </span>
                          <span className="text-green-400 text-sm">{formatCurrency(report.total_net_royalty || 0)}</span>
                        </div>
                      </div>
                      {/* Botones de acción para el reporte */}
                      {report.status !== 'paid' && (
                        <div className="flex gap-2 mt-2 pt-2 border-t border-white/10">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateReportStatus(report.id, 'approved');
                            }}
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Aprobar
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateReportStatus(report.id, 'paid');
                            }}
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs border-green-500/50 text-green-400 hover:bg-green-500/20"
                          >
                            <DollarSign className="w-3 h-3 mr-1" />
                            Pagado
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteReport(report.id);
                            }}
                            size="sm"
                            variant="outline"
                            className="text-xs border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Report Detail */}
          <Card className="card-viubux-dark border-purple-500/30 lg:col-span-1">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Detalle de Ventas
                </CardTitle>
                {selectedReport && selectedReport.status !== 'paid' && (
                  <Button
                    onClick={() => setShowAddEntry(true)}
                    size="sm"
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Entrada
                  </Button>
                )}
              </div>
              {/* Botones de acciones rápidas */}
              <div className="flex flex-wrap gap-2 mt-3">
                <Button
                  onClick={() => setShowDirectSaleModal(true)}
                  size="sm"
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid="direct-sale-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Venta Directa
                </Button>
                <Button
                  onClick={() => setShowWebhookSaleModal(true)}
                  size="sm"
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="webhook-sale-btn"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Venta Webhook
                </Button>
                <Button
                  onClick={() => setShowPoolContributionModal(true)}
                  size="sm"
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid="pool-contribution-btn"
                >
                  <Users className="w-4 h-4 mr-1" />
                  Aporte al Pool
                </Button>
              </div>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              {!selectedReport ? (
                <p className="text-white/40 text-center py-8">Selecciona un reporte</p>
              ) : (
                <div className="space-y-4">
                  {/* Totals */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-green-500/10 rounded p-2">
                      <p className="text-white/50 text-xs">Neto</p>
                      <p className="text-green-400 font-bold text-sm">{formatCurrency(selectedReport.total_net_royalty || 0)}</p>
                    </div>
                    <div className="bg-blue-500/10 rounded p-2">
                      <p className="text-white/50 text-xs">Bruto</p>
                      <p className="text-blue-400 font-bold text-sm">{formatCurrency(selectedReport.total_gross || 0)}</p>
                    </div>
                    <div className="bg-purple-500/10 rounded p-2">
                      <p className="text-white/50 text-xs">Unidades</p>
                      <p className="text-purple-400 font-bold text-sm">{selectedReport.total_units || 0}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  {selectedReport.status !== 'paid' && (
                    <div className="flex gap-2">
                      {selectedReport.status === 'draft' && selectedReport.entries?.length > 0 && (
                        <Button onClick={handlePublishReport} size="sm" className="flex-1 bg-blue-500 hover:bg-blue-600">
                          Publicar
                        </Button>
                      )}
                      {selectedReport.status === 'published' && (
                        <Button onClick={handleMarkPaid} size="sm" className="flex-1 bg-green-500 hover:bg-green-600">
                          Marcar Pagado
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Entries */}
                  <div className="space-y-2">
                    {selectedReport.entries?.map((entry, idx) => (
                      <div key={entry.id || idx} className="bg-black/30 rounded p-3 text-sm">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium">{entry.work_title}</p>
                              {entry.is_direct_sale && (
                                <span className="bg-purple-500/20 text-purple-400 text-[10px] px-1.5 py-0.5 rounded">
                                  DIRECTA
                                </span>
                              )}
                            </div>
                            <p className="text-white/50 text-xs font-mono">{entry.isbn}</p>
                            <p className="text-purple-400 text-xs">{entry.platform}</p>
                          </div>
                          {selectedReport.status !== 'paid' && (
                            <button
                              onClick={() => handleDeleteEntry(selectedReport.id, entry.id)}
                              disabled={deletingEntry === entry.id}
                              className="text-red-400 hover:text-red-300 disabled:opacity-50 p-1"
                              title="Eliminar venta"
                            >
                              {deletingEntry === entry.id ? (
                                <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                        <div className="flex justify-between mt-2 text-xs">
                          <span className="text-white/50">{entry.units_sold} uds</span>
                          <div className="text-right">
                            <span className="text-green-400 font-medium">{formatCurrency(entry.net_royalty_mxn || entry.net_royalty)}</span>
                            {entry.currency && entry.currency !== 'MXN' && (
                              <span className="text-white/40 ml-1">({formatCurrency(entry.gross_income)} {entry.currency})</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import CSV Section */}
      {activeSection === 'import' && (
        <>
          {/* Publica.la Sync Status */}
          <Card className="card-viubux-dark border-blue-500/30 mb-4">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-blue-400" />
                  Sincronización con Publica.la
                </div>
                <Button
                  onClick={handleSyncPublicala}
                  disabled={syncingPublicala}
                  className="bg-blue-500 hover:bg-blue-600 text-white text-sm"
                  data-testid="sync-publicala-btn"
                >
                  {syncingPublicala ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sincronizando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sincronizar Ahora
                    </>
                  )}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                <p className="text-yellow-400 text-sm">
                  <strong>⚠️ Importante:</strong> La API de Publica.la solo proporciona datos básicos de órdenes. 
                  Para las regalías detalladas (comisiones de plataforma, tipos de cambio, etc.), 
                  debes descargar el CSV desde el dashboard de Publica.la y subirlo manualmente aquí.
                </p>
              </div>
              
              {loadingPublicalaStatus ? (
                <div className="flex justify-center py-4">
                  <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : publicalaStatus?.months ? (
                <div className="space-y-3">
                  <p className="text-white/70 text-sm mb-2">Estado de reportes por mes:</p>
                  <div className="grid md:grid-cols-3 gap-3">
                    {publicalaStatus.months.slice(0, 6).map(month => (
                      <div 
                        key={month.period}
                        className={`rounded-lg p-3 border ${
                          month.status === 'uploaded' 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : 'bg-orange-500/10 border-orange-500/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">{month.month_name} {month.year}</span>
                          {month.status === 'uploaded' ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Clock className="w-4 h-4 text-orange-400" />
                          )}
                        </div>
                        <div className="text-xs space-y-1">
                          <div className="flex justify-between text-white/60">
                            <span>Reportes:</span>
                            <span className="text-white">{month.royalty_reports}</span>
                          </div>
                          <div className="flex justify-between text-white/60">
                            <span>Ventas sync:</span>
                            <span className="text-white">{month.synced_sales}</span>
                          </div>
                          {month.uploaded_files?.length > 0 && (
                            <div className="text-green-400 text-[10px] mt-1">
                              📁 {month.uploaded_files.length} archivo(s) subido(s)
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-white/50 text-center py-4">Cargando estado...</p>
              )}
            </CardContent>
          </Card>

          {/* CSV Import Form */}
          <Card className="card-viubux-dark border-purple-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Upload className="w-5 h-5 text-green-400" />
                Importar CSV/Excel de Ventas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-400 text-sm mb-2">Formatos de archivo soportados:</p>
                <div className="grid md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="text-white font-semibold mb-1">Formato Básico (CSV):</p>
                    <code className="text-white/70 bg-black/30 p-2 rounded block">
                      ISBN,Platform,Units,GrossIncome<br/>
                      VBX-001,Audible,150,25000<br/>
                      978-123,Viubux,200,35000
                    </code>
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-1">Formato Completo (CSV/Excel):</p>
                    <code className="text-white/70 bg-black/30 p-2 rounded block">
                      ISBN,Title,AuthorEmail,Platform,Units,GrossIncome<br/>
                      VBX-001,Mi Libro,autor@email.com,Audible,150,25000
                    </code>
                  </div>
              </div>
              <p className="text-white/50 text-xs mt-2">
                Columnas alternativas: Titulo, Autor, Plataforma, Unidades, Ventas, Ingreso, Bruto
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Tipo de Período</label>
                <select
                  value={importConfig.period_type}
                  onChange={(e) => setImportConfig({...importConfig, period_type: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                >
                  <option value="monthly">Mensual</option>
                  <option value="quarterly">Trimestral</option>
                  <option value="semiannual">Semestral</option>
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Moneda</label>
                <select
                  value={importConfig.currency}
                  onChange={(e) => setImportConfig({...importConfig, currency: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                >
                  <option value="MXN">MXN - Peso Mexicano</option>
                  <option value="USD">USD - Dólar</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Autor (opcional)</label>
                <select
                  value={importConfig.author_id}
                  onChange={(e) => setImportConfig({...importConfig, author_id: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                >
                  <option value="">Detectar del archivo...</option>
                  {authors.map(a => (
                    <option key={a.id} value={a.id}>{a.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Fecha Inicio</label>
                <input
                  type="date"
                  value={importConfig.period_start}
                  onChange={(e) => setImportConfig({...importConfig, period_start: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Fecha Fin</label>
                <input
                  type="date"
                  value={importConfig.period_end}
                  onChange={(e) => setImportConfig({...importConfig, period_end: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-white cursor-pointer">
                  <input
                    type="checkbox"
                    checked={importConfig.create_works}
                    onChange={(e) => setImportConfig({...importConfig, create_works: e.target.checked})}
                    className="w-5 h-5 rounded bg-black/30 border-purple-500/30"
                  />
                  <span className="text-sm">Crear obras automáticamente</span>
                </label>
              </div>
            </div>

            <div className="border-2 border-dashed border-purple-500/30 rounded-lg p-8 text-center">
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv,.xlsx,.xls"
                onChange={(e) => {
                  setImportFile(e.target.files[0]);
                  setImportErrors([]);
                  setDuplicateWarning(null);
                  setForceUpload(false);
                }}
                className="hidden"
                id="csv-upload"
                data-testid="csv-file-input"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                <p className="text-white">
                  {importFile ? importFile.name : 'Haz clic para seleccionar archivo CSV o Excel'}
                </p>
                <p className="text-white/50 text-sm mt-2">.csv, .xlsx, .xls soportados</p>
              </label>
            </div>

            {importSummary && (
              <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/10 border border-green-500/30 rounded-xl p-4" data-testid="import-summary">
                <p className="gotham-bold text-white text-sm mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Resumen de la importación ({importSummary.processed} ventas)
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-black/30 rounded-lg p-3">
                    <p className="text-white/50 text-xs mb-1">Ingreso bruto</p>
                    <p className="text-white gotham-bold">{formatCurrency(importSummary.total_gross_mxn)}</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3">
                    <p className="text-yellow-300/70 text-xs mb-1">Retención internacional (10%)</p>
                    <p className="text-yellow-300 gotham-bold">{formatCurrency(importSummary.total_international_withholding_mxn)}</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3">
                    <p className="text-blue-300/70 text-xs mb-1">5BX Distribution (15%)</p>
                    <p className="text-blue-300 gotham-bold">{formatCurrency(importSummary.total_distributor_5bx_mxn)}</p>
                  </div>
                  {importSummary.total_platform_viubux_mxn > 0 && (
                    <div className="bg-black/30 rounded-lg p-3">
                      <p className="text-purple-300/70 text-xs mb-1">Viubux (33%)</p>
                      <p className="text-purple-300 gotham-bold">{formatCurrency(importSummary.total_platform_viubux_mxn)}</p>
                    </div>
                  )}
                  <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                    <p className="text-green-300/70 text-xs mb-1">Regalías netas autores</p>
                    <p className="text-green-300 gotham-bold">{formatCurrency(importSummary.total_net_royalty_mxn)}</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3">
                    <p className="text-white/50 text-xs mb-1">Autores acreditados / pendientes</p>
                    <p className="text-white gotham-bold">{importSummary.authors_credited} / <span className="text-orange-400">{importSummary.pending_reconciliation}</span></p>
                  </div>
                </div>
                {importSummary.pending_reconciliation > 0 && (
                  <p className="text-orange-300/80 text-xs mt-3">
                    ⚠️ {importSummary.pending_reconciliation} venta(s) con ISBN no asociado quedaron en <strong>Conciliación</strong> para asignación manual.
                  </p>
                )}
              </div>
            )}

            {importErrors.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 max-h-40 overflow-y-auto">
                <p className="text-red-400 text-sm font-semibold mb-2">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Errores durante la importación:
                </p>
                <ul className="text-red-300 text-xs space-y-1">
                  {importErrors.slice(0, 20).map((err, idx) => (
                    <li key={idx}>• {err}</li>
                  ))}
                  {importErrors.length > 20 && (
                    <li className="text-white/50">... y {importErrors.length - 20} errores más</li>
                  )}
                </ul>
              </div>
            )}

            <Button
              onClick={() => handleImportCSV(false)}
              disabled={importing || checkingDuplicates}
              className={`w-full ${!importFile || !importConfig.period_start || !importConfig.period_end ? 'bg-gray-600 hover:bg-gray-500' : 'btn-viubux'}`}
              data-testid="import-csv-btn"
            >
              {checkingDuplicates ? '🔍 Verificando duplicados...' :
               importing ? '⏳ Importando...' : 
               !importFile ? '📁 Selecciona un archivo primero' :
               !importConfig.period_start || !importConfig.period_end ? '📅 Configura el período' :
               '✅ Importar y Procesar Archivo'}
            </Button>
          </CardContent>
        </Card>
        </>
      )}

      {/* Platforms Section */}
      {activeSection === 'platforms' && (
        <Card className="card-viubux-dark border-purple-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-400" />
              Plataformas de Distribución
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newPlatform}
                onChange={(e) => setNewPlatform(e.target.value)}
                placeholder="Nueva plataforma..."
                className="flex-1 bg-black/30 border border-purple-500/30 rounded-lg px-4 py-2 text-white"
              />
              <Button onClick={handleAddPlatform} className="btn-viubux">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              {platforms.map(platform => (
                <div key={platform.id} className="bg-black/30 border border-purple-500/20 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-white">{platform.name}</p>
                    {platform.is_viubux && (
                      <span className="text-xs text-green-400">+33% fee</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeletePlatform(platform.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Sales Section */}
      {activeSection === 'pending' && (
        <div className="space-y-6">
          {/* Info Banner */}
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-orange-400 font-semibold">Ventas sin Autor Asignado</p>
                <p className="text-white/70 text-sm mt-1">
                  Estas ventas fueron importadas desde CSV pero el ISBN no tiene autor registrado en la plataforma. 
                  Cuando un autor registre una obra con este ISBN, las ventas se le asignarán automáticamente. 
                  También puedes asignarlas manualmente.
                </p>
              </div>
            </div>
          </div>

          {loadingPending ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-white/60">Cargando ventas pendientes...</p>
            </div>
          ) : pendingSummary.length === 0 ? (
            <Card className="card-viubux-dark border-green-500/30">
              <CardContent className="py-12 text-center">
                <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <p className="text-white text-lg">No hay ventas pendientes</p>
                <p className="text-white/60 text-sm mt-2">Todas las ventas importadas tienen autor asignado</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Summary by ISBN */}
              <Card className="card-viubux-dark border-orange-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-400" />
                    Resumen por ISBN ({pendingSummary.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto">
                  <div className="space-y-3">
                    {pendingSummary.map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() => setSelectedPendingIsbn(item._id)}
                        className={`p-4 rounded-lg cursor-pointer transition-all ${
                          selectedPendingIsbn === item._id
                            ? 'bg-orange-500/30 border border-orange-500'
                            : 'bg-black/30 border border-transparent hover:border-orange-500/50'
                        }`}
                        data-testid={`pending-isbn-${item._id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-mono text-sm">{item._id}</p>
                            <p className="text-white/70 text-sm mt-1 truncate">{item.work_title}</p>
                          </div>
                          <Button
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white ml-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssigningIsbn(item._id);
                              setShowAssignModal(true);
                            }}
                            data-testid={`assign-btn-${item._id}`}
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <span className="text-orange-400">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {item.count} ventas
                          </span>
                          <span className="text-green-400">
                            <DollarSign className="w-3 h-3 inline" />
                            {formatCurrency(item.total_net_royalty || 0)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Details for selected ISBN */}
              <Card className="card-viubux-dark border-orange-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-400" />
                    Detalle de Ventas
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto">
                  {!selectedPendingIsbn ? (
                    <p className="text-white/40 text-center py-8">Selecciona un ISBN para ver detalles</p>
                  ) : (
                    <div className="space-y-3">
                      {pendingSales
                        .filter(s => s.isbn === selectedPendingIsbn)
                        .slice(0, 50)
                        .map((sale, idx) => (
                          <div key={sale.id || idx} className="bg-black/30 rounded-lg p-3 text-sm">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-white">{sale.entry?.work_title || sale.isbn}</p>
                                <p className="text-white/50 text-xs">{sale.entry?.platform}</p>
                              </div>
                              <span className="text-green-400 font-mono">
                                ${(sale.entry?.net_royalty || 0).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between mt-2 text-xs text-white/40">
                              <span>Período: {sale.period_start} - {sale.period_end}</span>
                              {sale.entry?.transaction_date && (
                                <span>{sale.entry.transaction_date}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      {pendingSales.filter(s => s.isbn === selectedPendingIsbn).length > 50 && (
                        <p className="text-white/50 text-center text-sm py-2">
                          ... y {pendingSales.filter(s => s.isbn === selectedPendingIsbn).length - 50} más
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Works Section */}
      {activeSection === 'works' && (
        <div className="space-y-6">
          {/* Header y Acciones */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-white text-lg gotham-bold flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-400" />
                Gestión de Obras
              </h3>
              <p className="text-white/60 text-sm">
                {orphanCount > 0 ? (
                  <span className="text-orange-400">{orphanCount} obras sin autor asignado</span>
                ) : (
                  'Todas las obras tienen autor asignado'
                )}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowOrphansOnly(!showOrphansOnly)}
                variant="outline"
                className={`border-white/20 ${showOrphansOnly ? 'bg-orange-500/20 text-orange-400 border-orange-500/50' : 'text-white'}`}
                size="sm"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                {showOrphansOnly ? 'Solo Huérfanas' : 'Todas las Obras'}
              </Button>
              <Button
                onClick={() => {
                  setNewWork({
                    title: '',
                    isbn: generateVbxIsbn(),
                    author_id: '',
                    formats: [],
                    public_url: '',
                    cover_image: ''
                  });
                  setShowCreateWorkModal(true);
                }}
                className="btn-viubux"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Obra
              </Button>
            </div>
          </div>

          {/* Buscador */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              value={workSearch}
              onChange={(e) => setWorkSearch(e.target.value)}
              className="w-full bg-black/30 border border-purple-500/30 rounded-lg pl-10 pr-4 py-2 text-white text-sm"
              placeholder="Buscar por título o ISBN..."
            />
          </div>

          {/* Lista de Obras */}
          <Card className="card-viubux-dark border-purple-500/30">
            <CardContent className="p-0">
              {loadingWorks ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-white/60 mt-3">Cargando obras...</p>
                </div>
              ) : filteredWorks.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40">
                    {showOrphansOnly ? 'No hay obras huérfanas' : 'No hay obras registradas'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-white/10">
                  {filteredWorks.map(work => (
                    <div 
                      key={work.id} 
                      className="p-4 hover:bg-white/5 transition-colors"
                      data-testid={`work-item-${work.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h4 className="text-white font-medium">{work.title}</h4>
                            {(!work.author_id || work.is_orphan) && (
                              <span className="bg-orange-500/20 text-orange-400 text-xs px-2 py-0.5 rounded-full">
                                Sin autor
                              </span>
                            )}
                            {work.has_coproduction && (
                              <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                Coproducción ({work.coproducers?.length || 0})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-white/50">
                            <span className="font-mono">{work.isbn}</span>
                            {work.formats?.length > 0 && (
                              <span>{renderFormats(work.formats)}</span>
                            )}
                            {work.author_id && !work.is_orphan && (
                              <span className="text-green-400">
                                Asignado a: {authors.find(a => a.id === work.author_id)?.full_name || 'Autor'}
                              </span>
                            )}
                          </div>
                          {/* Mostrar coproductores si existen */}
                          {work.has_coproduction && work.coproducers?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {work.coproducers.map((cp, idx) => (
                                <span key={idx} className="bg-white/5 text-white/70 text-xs px-2 py-1 rounded">
                                  {cp.full_name || 'Usuario'}: {cp.percentage}% ({cp.role})
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Botón Coproductores - solo si tiene autor asignado */}
                          {work.author_id && !work.is_orphan && (
                            <Button
                              onClick={() => openCoproducersModal(work)}
                              size="sm"
                              variant="outline"
                              className={`border-blue-500/50 text-blue-400 hover:bg-blue-500/20 ${work.has_coproduction ? 'bg-blue-500/10' : ''}`}
                              data-testid={`coproducers-btn-${work.id}`}
                            >
                              <Users className="w-4 h-4 mr-1" />
                              Coproductores
                            </Button>
                          )}
                          {(!work.author_id || work.is_orphan) && (
                            <Button
                              onClick={() => {
                                setSelectedWork(work);
                                setShowAssignWorkModal(true);
                              }}
                              size="sm"
                              className="bg-green-500 hover:bg-green-600 text-white"
                              data-testid={`assign-work-btn-${work.id}`}
                            >
                              <UserPlus className="w-4 h-4 mr-1" />
                              Asignar Autor
                            </Button>
                          )}
                          {/* Botón Eliminar Obra */}
                          <Button
                            onClick={() => handleDeleteWork(work)}
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                            data-testid={`delete-work-btn-${work.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== SALES HISTORY SECTION ===== */}
      {activeSection === 'history' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-viubux-dark border-emerald-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-emerald-400 text-3xl gotham-bold">{salesHistorySummary.totalSales || 0}</p>
                <p className="text-white/60 text-sm">Total Ventas</p>
              </CardContent>
            </Card>
            <Card className="card-viubux-dark border-blue-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-blue-400 text-3xl gotham-bold">{(salesHistorySummary.totalUnits || 0).toLocaleString()}</p>
                <p className="text-white/60 text-sm">Unidades Vendidas</p>
              </CardContent>
            </Card>
            <Card className="card-viubux-dark border-yellow-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-yellow-400 text-3xl gotham-bold">${(salesHistorySummary.totalGrossIncome || 0).toLocaleString()}</p>
                <p className="text-white/60 text-sm">Ingreso Bruto Total</p>
              </CardContent>
            </Card>
            <Card className="card-viubux-dark border-green-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-green-400 text-3xl gotham-bold">${(salesHistorySummary.totalNetRoyalties || 0).toLocaleString()}</p>
                <p className="text-white/60 text-sm">Regalías Netas Total</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="card-viubux-dark border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Search className="w-5 h-5 text-white/60" />
                Filtros de Búsqueda
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <label className="block text-white/60 text-xs mb-1">Desde</label>
                  <input
                    type="date"
                    value={salesHistoryFilters.dateFrom}
                    onChange={(e) => setSalesHistoryFilters({...salesHistoryFilters, dateFrom: e.target.value})}
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-xs mb-1">Hasta</label>
                  <input
                    type="date"
                    value={salesHistoryFilters.dateTo}
                    onChange={(e) => setSalesHistoryFilters({...salesHistoryFilters, dateTo: e.target.value})}
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-xs mb-1">Plataforma</label>
                  <select
                    value={salesHistoryFilters.platform}
                    onChange={(e) => setSalesHistoryFilters({...salesHistoryFilters, platform: e.target.value})}
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="">Todas</option>
                    {platforms.map(p => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-xs mb-1">Autor</label>
                  <select
                    value={salesHistoryFilters.author_id}
                    onChange={(e) => setSalesHistoryFilters({...salesHistoryFilters, author_id: e.target.value})}
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="">Todos</option>
                    {authors.map(a => (
                      <option key={a.id} value={a.id}>{a.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-xs mb-1">Buscar</label>
                  <input
                    type="text"
                    value={salesHistoryFilters.search}
                    onChange={(e) => setSalesHistoryFilters({...salesHistoryFilters, search: e.target.value})}
                    placeholder="ISBN, título..."
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button 
                  onClick={fetchSalesHistory} 
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  size="sm"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Buscar
                </Button>
                <Button 
                  onClick={() => {
                    setSalesHistoryFilters({ dateFrom: '', dateTo: '', platform: '', author_id: '', search: '' });
                    setTimeout(fetchSalesHistory, 100);
                  }} 
                  variant="outline"
                  className="border-white/20 text-white"
                  size="sm"
                >
                  Limpiar Filtros
                </Button>
                <Button 
                  onClick={downloadSalesHistoryCSV}
                  disabled={salesHistory.length === 0}
                  className="bg-blue-500 hover:bg-blue-600 text-white ml-auto"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary by Platform */}
          {Object.keys(salesHistorySummary.byPlatform || {}).length > 0 && (
            <Card className="card-viubux-dark border-indigo-500/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg">📊 Resumen por Plataforma</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(salesHistorySummary.byPlatform || {}).map(([platform, data]) => (
                    <div key={platform} className="bg-black/30 rounded-lg p-3 border border-white/10">
                      <p className="text-indigo-400 font-bold text-sm">{platform}</p>
                      <p className="text-white text-lg">{data.units?.toLocaleString() || 0} uds</p>
                      <p className="text-white/60 text-xs">${(data.gross || 0).toLocaleString()} bruto</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary by Author */}
          {Object.keys(salesHistorySummary.byAuthor || {}).length > 0 && (
            <Card className="card-viubux-dark border-purple-500/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg">👤 Resumen por Autor</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 text-white/60">Autor</th>
                        <th className="text-right py-2 text-white/60">Obras</th>
                        <th className="text-right py-2 text-white/60">Unidades</th>
                        <th className="text-right py-2 text-white/60">Ingreso Bruto</th>
                        <th className="text-right py-2 text-white/60">Regalía Neta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(salesHistorySummary.byAuthor || {}).slice(0, 10).map(([authorId, data]) => (
                        <tr key={authorId} className="border-b border-white/5">
                          <td className="py-2 text-white">{data.name || 'Desconocido'}</td>
                          <td className="py-2 text-right text-white/80">{data.works || 0}</td>
                          <td className="py-2 text-right text-blue-400">{(data.units || 0).toLocaleString()}</td>
                          <td className="py-2 text-right text-yellow-400">${(data.gross || 0).toLocaleString()}</td>
                          <td className="py-2 text-right text-green-400">${(data.net || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sales Table */}
          <Card className="card-viubux-dark border-emerald-500/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  📋 Historial de Ventas ({salesHistory.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {loadingSalesHistory ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                  <p className="text-white/60">Cargando historial...</p>
                </div>
              ) : salesHistory.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/60">No hay ventas registradas</p>
                  <p className="text-white/40 text-sm">Importa datos desde la pestaña &quot;Importar CSV&quot;</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 text-xs">
                        <th className="text-left py-2 px-2 text-white/60">Fecha</th>
                        <th className="text-left py-2 px-2 text-white/60">ID</th>
                        <th className="text-left py-2 px-2 text-white/60">Obra</th>
                        <th className="text-left py-2 px-2 text-white/60">ISBN</th>
                        <th className="text-left py-2 px-2 text-white/60">Autor</th>
                        <th className="text-left py-2 px-2 text-white/60">Plataforma</th>
                        <th className="text-right py-2 px-2 text-white/60">Uds</th>
                        <th className="text-right py-2 px-2 text-white/60">Bruto</th>
                        <th className="text-right py-2 px-2 text-white/60">Neto</th>
                        <th className="text-left py-2 px-2 text-white/60">Periodo</th>
                        <th className="text-center py-2 px-2 text-white/60">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesHistory.slice(0, 100).map((sale, idx) => (
                        <tr key={sale.id || idx} className={`border-b border-white/5 hover:bg-white/5 ${sale.is_coproduction_share ? 'bg-rose-500/5' : ''}`}>
                          <td className="py-2 px-2 text-white/70 text-xs">{sale.created_at?.substring(0, 10)}</td>
                          <td className="py-2 px-2 text-white/50 font-mono text-xs">{(sale.id || sale.transaction_id || '').substring(0, 8)}...</td>
                          <td className="py-2 px-2 text-white max-w-[150px] truncate" title={sale.work_title || sale.content_name}>
                            {sale.work_title || sale.content_name || '-'}
                            {sale.is_coproduction_share && (
                              <span className="ml-1 px-1 py-0.5 text-[10px] bg-rose-500/30 text-rose-300 rounded">
                                {sale.coproduction_percentage}% {sale.coproduction_role === 'autor_principal' ? 'Principal' : 'Co-Prod'}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-blue-400 font-mono text-xs">{sale.isbn || '-'}</td>
                          <td className="py-2 px-2 text-purple-400 text-xs">{sale.author_name || '-'}</td>
                          <td className="py-2 px-2 text-indigo-400 text-xs">{sale.platform}</td>
                          <td className="py-2 px-2 text-right text-white">{sale.is_coproduction_share ? '-' : sale.units_sold}</td>
                          <td className="py-2 px-2 text-right text-yellow-400">{sale.is_coproduction_share ? '-' : `$${(sale.gross_income || 0).toFixed(2)}`}</td>
                          <td className="py-2 px-2 text-right text-green-400">${(sale.net_royalty || 0).toFixed(2)}</td>
                          <td className="py-2 px-2 text-white/50 text-xs">{sale.period || '-'}</td>
                          <td className="py-2 px-2 text-center">
                            <button
                              onClick={() => handleDeleteSale(sale)}
                              className="text-red-400 hover:text-red-300 p-1 hover:bg-red-500/20 rounded"
                              title="Eliminar venta"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {salesHistory.length > 100 && (
                    <p className="text-center text-white/40 text-xs mt-4">
                      Mostrando 100 de {salesHistory.length} registros. Descarga el CSV para ver todos.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Report Modal */}
      {showCreateReport && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-white/10">
              <h2 className="gotham-bold text-xl text-white">Crear Reporte de Regalías</h2>
              <p className="text-white/60 text-sm">Para: {selectedAuthor?.full_name}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Tipo de Período</label>
                <select
                  value={newReport.period_type}
                  onChange={(e) => setNewReport({...newReport, period_type: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                >
                  <option value="monthly">Mensual</option>
                  <option value="quarterly">Trimestral</option>
                  <option value="semiannual">Semestral</option>
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Fecha Inicio</label>
                <input
                  type="date"
                  value={newReport.period_start}
                  onChange={(e) => setNewReport({...newReport, period_start: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Fecha Fin</label>
                <input
                  type="date"
                  value={newReport.period_end}
                  onChange={(e) => setNewReport({...newReport, period_end: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                />
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <Button onClick={() => setShowCreateReport(false)} variant="outline" className="flex-1 border-white/20 text-white">
                Cancelar
              </Button>
              <Button onClick={handleCreateReport} className="flex-1 btn-viubux">
                Crear Reporte
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Entry Modal */}
      {showAddEntry && selectedReport && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-white/10">
              <h2 className="gotham-bold text-xl text-white">Agregar Entrada de Ventas</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">ISBN / Identificador</label>
                <input
                  type="text"
                  value={newEntry.isbn}
                  onChange={(e) => setNewEntry({...newEntry, isbn: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white font-mono"
                  placeholder="VBX-20260324 o ISBN"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Plataforma</label>
                <select
                  value={newEntry.platform}
                  onChange={(e) => setNewEntry({...newEntry, platform: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                >
                  <option value="">Seleccionar...</option>
                  {platforms.map(p => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Unidades Vendidas</label>
                  <input
                    type="number"
                    value={newEntry.units_sold}
                    onChange={(e) => setNewEntry({...newEntry, units_sold: parseInt(e.target.value) || 0})}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Ingreso Bruto</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newEntry.gross_income}
                    onChange={(e) => setNewEntry({...newEntry, gross_income: parseFloat(e.target.value) || 0})}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Moneda</label>
                  <select
                    value={newEntry.currency}
                    onChange={(e) => setNewEntry({...newEntry, currency: e.target.value})}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="MXN">MXN</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Tipo de Cambio</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newEntry.exchange_rate}
                    onChange={(e) => setNewEntry({...newEntry, exchange_rate: parseFloat(e.target.value) || 1})}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <Button onClick={() => setShowAddEntry(false)} variant="outline" className="flex-1 border-white/20 text-white">
                Cancelar
              </Button>
              <Button onClick={handleAddEntry} className="flex-1 btn-viubux">
                Agregar Entrada
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Sales Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-orange-500/30 rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-white/10">
              <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-orange-400" />
                Asignar Ventas a Autor
              </h2>
              <p className="text-white/60 text-sm mt-1">
                ISBN: <span className="font-mono text-orange-400">{assigningIsbn}</span>
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Summary of sales */}
              {assigningIsbn && pendingSummary.find(s => s._id === assigningIsbn) && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Ventas pendientes:</span>
                    <span className="text-orange-400 font-semibold">
                      {pendingSummary.find(s => s._id === assigningIsbn)?.count || 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-white/70">Regalías totales:</span>
                    <span className="text-green-400 font-semibold">
                      {formatCurrency(pendingSummary.find(s => s._id === assigningIsbn)?.total_net_royalty || 0)}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-white/70 text-sm mb-2">Seleccionar Autor</label>
                <select
                  value={selectedAuthorForAssign}
                  onChange={(e) => setSelectedAuthorForAssign(e.target.value)}
                  className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                  data-testid="assign-author-select"
                >
                  <option value="">-- Selecciona un autor --</option>
                  {authors.map(a => (
                    <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-400 text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Al asignar, se crearán o actualizarán los reportes de regalías del autor para los períodos correspondientes.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <Button 
                onClick={() => {
                  setShowAssignModal(false);
                  setAssigningIsbn(null);
                  setSelectedAuthorForAssign('');
                }} 
                variant="outline" 
                className="flex-1 border-white/20 text-white"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAssignSales} 
                disabled={!selectedAuthorForAssign || assigning}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                data-testid="confirm-assign-btn"
              >
                {assigning ? 'Asignando...' : 'Asignar Ventas'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Work Modal */}
      {showAssignWorkModal && selectedWork && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-green-500/30 rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-white/10">
              <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-green-400" />
                Asignar Obra a Autor
              </h2>
              <p className="text-white/60 text-sm mt-1">
                Obra: <span className="text-green-400">{selectedWork.title}</span>
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Work details */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-white/70">ISBN:</span>
                    <span className="text-white font-mono">{selectedWork.isbn}</span>
                  </div>
                  {selectedWork.formats?.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-white/70">Formatos:</span>
                      <span className="text-white">{renderFormats(selectedWork.formats)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">Seleccionar Autor</label>
                <select
                  value={selectedAuthorForWork}
                  onChange={(e) => setSelectedAuthorForWork(e.target.value)}
                  className="w-full bg-black/30 border border-green-500/30 rounded-lg px-4 py-3 text-white"
                  data-testid="assign-work-author-select"
                >
                  <option value="">-- Selecciona un autor --</option>
                  {authors.map(a => (
                    <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-400 text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Al asignar la obra, el autor recibirá una notificación y podrá ver la obra en su perfil.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <Button 
                onClick={() => {
                  setShowAssignWorkModal(false);
                  setSelectedWork(null);
                  setSelectedAuthorForWork('');
                }} 
                variant="outline" 
                className="flex-1 border-white/20 text-white"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAssignWork} 
                disabled={!selectedAuthorForWork || assigningWork}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                data-testid="confirm-assign-work-btn"
              >
                {assigningWork ? 'Asignando...' : 'Asignar Obra'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Work Modal */}
      {showCreateWorkModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-blue-500/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-400" />
                Crear Nueva Obra
              </h2>
              <p className="text-white/60 text-sm mt-1">
                Registra una nueva obra y asígnala a un autor
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Título de la Obra *</label>
                <input
                  type="text"
                  value={newWork.title}
                  onChange={(e) => setNewWork({...newWork, title: e.target.value})}
                  className="w-full bg-black/30 border border-blue-500/30 rounded-lg px-4 py-3 text-white"
                  placeholder="Ej: Mi Primer Audiolibro"
                  data-testid="new-work-title"
                />
              </div>

              {/* ISBN Mode Toggle */}
              <div>
                <label className="block text-white/70 text-sm mb-2">ISBN</label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsbnMode('auto');
                      setNewWork({...newWork, isbn: ''});
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-2 ${
                      isbnMode === 'auto'
                        ? 'bg-violet-500 text-white'
                        : 'bg-black/30 text-white/60 border border-white/20 hover:border-violet-500/50'
                    }`}
                  >
                    <Wand2 className="w-4 h-4" />
                    Generar Automático
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsbnMode('manual');
                      setNewWork({...newWork, isbn: ''});
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-2 ${
                      isbnMode === 'manual'
                        ? 'bg-blue-500 text-white'
                        : 'bg-black/30 text-white/60 border border-white/20 hover:border-blue-500/50'
                    }`}
                  >
                    <FileEdit className="w-4 h-4" />
                    ISBN Externo
                  </button>
                </div>

                {isbnMode === 'auto' ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newWork.isbn}
                      className="flex-1 bg-black/30 border border-violet-500/30 rounded-lg px-4 py-3 text-white font-mono text-sm"
                      placeholder="VBX-20250604121530"
                      data-testid="new-work-isbn"
                      readOnly
                    />
                    <Button
                      type="button"
                      onClick={() => setNewWork({...newWork, isbn: generateVbxIsbn()})}
                      className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white px-4"
                      data-testid="generate-isbn-btn"
                    >
                      <Wand2 className="w-4 h-4 mr-1" />
                      Generar
                    </Button>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={newWork.isbn}
                    onChange={(e) => setNewWork({...newWork, isbn: e.target.value})}
                    className="w-full bg-black/30 border border-blue-500/30 rounded-lg px-4 py-3 text-white font-mono text-sm"
                    placeholder="Ingresa el ISBN externo (ej: 978-84-1234-567-8)"
                    data-testid="new-work-isbn-manual"
                  />
                )}
                
                {newWork.isbn && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-green-400 text-xs flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      ISBN {isbnMode === 'auto' ? 'generado' : 'ingresado'}:
                    </span>
                    <code className="bg-black/50 px-2 py-0.5 rounded text-blue-400 font-mono text-sm">
                      {newWork.isbn}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(newWork.isbn);
                        toast.success('ISBN copiado al portapapeles');
                      }}
                      className="text-white/50 hover:text-white transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">Tipo de Contenido *</label>
                <select
                  value={newWork.content_type}
                  onChange={(e) => setNewWork({...newWork, content_type: e.target.value})}
                  className="w-full bg-black/30 border border-blue-500/30 rounded-lg px-4 py-3 text-white"
                  data-testid="new-work-content-type"
                >
                  <option value="audiobook">🎧 Audiolibro</option>
                  <option value="videobook">📹 Videolibro</option>
                  <option value="ebook">📖 eBook</option>
                  <option value="movie">🎬 Película</option>
                  <option value="music_video">🎵 Videoclip Musical</option>
                  <option value="conference">🎤 Conferencia</option>
                  <option value="book_presentation">📚 Presentación de Libro</option>
                  <option value="booktrailer">🎬 Booktrailer</option>
                  <option value="podcast">🎙️ Podcast</option>
                  <option value="song">🎵 Canción</option>
                  <option value="meditation">🧘 Meditación Guiada</option>
                </select>
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">Autor Principal *</label>
                <select
                  value={newWork.author_id}
                  onChange={(e) => setNewWork({...newWork, author_id: e.target.value})}
                  className="w-full bg-black/30 border border-blue-500/30 rounded-lg px-4 py-3 text-white"
                  data-testid="new-work-author"
                >
                  <option value="">-- Selecciona un autor --</option>
                  {authors.map(a => (
                    <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>
                  ))}
                </select>
              </div>

              {/* Co-Productores Section */}
              <div className="border border-rose-500/30 rounded-xl p-4 bg-rose-500/5">
                <label className="block text-white/70 text-sm mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-rose-400" />
                  Co-Productores (Opcional)
                </label>
                
                {/* Current coproducers list */}
                {newWork.coproducers.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {newWork.coproducers.map((coprod, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm">{coprod.user_name}</span>
                          <span className="text-rose-400 text-sm font-bold">{coprod.percentage}%</span>
                          <span className="text-white/40 text-xs">({coprod.role})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCoproducerFromNewWork(coprod.user_id)}
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="text-xs text-white/50 mt-1">
                      Total asignado: {(newWork.coproducers || []).reduce((sum, c) => sum + parseFloat(c.percentage || 0), 0).toFixed(1)}% 
                      | Autor principal: {(100 - (newWork.coproducers || []).reduce((sum, c) => sum + parseFloat(c.percentage || 0), 0)).toFixed(1)}%
                    </div>
                  </div>
                )}

                {/* Add new coproducer */}
                <div className="grid grid-cols-12 gap-2">
                  <select
                    value={newWorkCoproducer.user_id}
                    onChange={(e) => setNewWorkCoproducer({...newWorkCoproducer, user_id: e.target.value})}
                    className="col-span-5 bg-black/30 border border-rose-500/30 rounded-lg px-2 py-2 text-white text-sm"
                  >
                    <option value="">Co-productor...</option>
                    {authors.filter(a => a.id !== newWork.author_id && !newWork.coproducers.some(c => c.user_id === a.id)).map(a => (
                      <option key={a.id} value={a.id}>{a.full_name}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={newWorkCoproducer.percentage}
                    onChange={(e) => setNewWorkCoproducer({...newWorkCoproducer, percentage: e.target.value})}
                    className="col-span-3 bg-black/30 border border-rose-500/30 rounded-lg px-2 py-2 text-white text-sm text-center"
                    placeholder="%"
                    min="0.1"
                    max="99"
                    step="0.1"
                  />
                  <select
                    value={newWorkCoproducer.role}
                    onChange={(e) => setNewWorkCoproducer({...newWorkCoproducer, role: e.target.value})}
                    className="col-span-3 bg-black/30 border border-rose-500/30 rounded-lg px-1 py-2 text-white text-xs"
                  >
                    <option value="coproductor">Co-Productor</option>
                    <option value="coautor">Co-Autor</option>
                    <option value="coinvestor">Co-Inversionista</option>
                    <option value="narrator">Narrador</option>
                    <option value="editor">Editor</option>
                  </select>
                  <Button
                    type="button"
                    onClick={addCoproducerToNewWork}
                    disabled={!newWorkCoproducer.user_id || !newWorkCoproducer.percentage}
                    className="col-span-1 bg-rose-500 hover:bg-rose-600 text-white p-2"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">URL en Viubux</label>
                <input
                  type="url"
                  value={newWork.public_url}
                  onChange={(e) => setNewWork({...newWork, public_url: e.target.value})}
                  className="w-full bg-black/30 border border-blue-500/30 rounded-lg px-4 py-3 text-white"
                  placeholder="https://app.viubux.com/..."
                />
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">URL de Portada</label>
                <input
                  type="url"
                  value={newWork.cover_image}
                  onChange={(e) => setNewWork({...newWork, cover_image: e.target.value})}
                  className="w-full bg-black/30 border border-blue-500/30 rounded-lg px-4 py-3 text-white"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">Formatos Disponibles</label>
                <div className="flex flex-wrap gap-2">
                  {['audiobook', 'ebook', 'printed', 'videobook'].map(format => (
                    <button
                      key={format}
                      type="button"
                      onClick={() => {
                        const formats = newWork.formats.includes(format)
                          ? newWork.formats.filter(f => f !== format)
                          : [...newWork.formats, format];
                        setNewWork({...newWork, formats});
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        newWork.formats.includes(format)
                          ? 'bg-blue-500 text-white'
                          : 'bg-black/30 text-white/60 border border-white/20 hover:border-blue-500/50'
                      }`}
                    >
                      {format === 'audiobook' && '🎧 Audiolibro'}
                      {format === 'ebook' && '📖 eBook'}
                      {format === 'printed' && '📚 Impreso'}
                      {format === 'videobook' && '📹 Videolibro'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <Button 
                onClick={() => {
                  setShowCreateWorkModal(false);
                  resetNewWorkState();
                }} 
                variant="outline" 
                className="flex-1 border-white/20 text-white"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateWork} 
                disabled={!newWork.title || !newWork.author_id}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="create-work-btn"
              >
                Crear Obra
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Sale Modal */}
      {showDirectSaleModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-purple-400" />
                Registrar Venta Directa
              </h2>
              <p className="text-white/60 text-sm mt-1">
                Agregar una venta manualmente a un autor específico
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Author Selection */}
              <div>
                <label className="block text-white/70 text-sm mb-2">Autor *</label>
                <select
                  value={directSale.author_id}
                  onChange={(e) => handleDirectSaleAuthorChange(e.target.value)}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                  data-testid="direct-sale-author"
                >
                  <option value="">-- Selecciona un autor --</option>
                  {authors.map(a => (
                    <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>
                  ))}
                </select>
              </div>

              {/* Work/Book Selection - Only show after author selected */}
              {directSale.author_id && (
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    Libro/Obra {loadingAuthorWorks && <span className="text-purple-400">(cargando...)</span>}
                  </label>
                  <select
                    value={directSale.work_id}
                    onChange={(e) => setDirectSale({...directSale, work_id: e.target.value, format: ''})}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                    data-testid="direct-sale-work"
                    disabled={loadingAuthorWorks}
                  >
                    <option value="">-- Sin especificar / Otro --</option>
                    {authorWorks.map(w => (
                      <option key={w.id} value={w.id}>
                        {w.title} {w.isbn ? `(ISBN: ${w.isbn})` : ''}
                      </option>
                    ))}
                  </select>
                  {authorWorks.length === 0 && !loadingAuthorWorks && (
                    <p className="text-yellow-400/70 text-xs mt-1">
                      Este autor no tiene obras registradas. Puedes continuar sin seleccionar una obra.
                    </p>
                  )}
                </div>
              )}

              {/* Format Selection - Only show after work selected */}
              {directSale.work_id && (
                <div>
                  <label className="block text-white/70 text-sm mb-2">Formato *</label>
                  <select
                    value={directSale.format}
                    onChange={(e) => setDirectSale({...directSale, format: e.target.value})}
                    className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                    data-testid="direct-sale-format"
                  >
                    <option value="">-- Selecciona el formato --</option>
                    {getWorkFormats().length > 0 ? (
                      getWorkFormats().map(f => (
                        <option key={f.format || f} value={f.format || f}>
                          {f.format === 'audiobook' || f === 'audiobook' ? '🎧 Audiolibro' : 
                           f.format === 'ebook' || f === 'ebook' ? '📱 eBook' : 
                           f.format === 'printed' || f === 'printed' ? '📖 Impreso' : 
                           f.format === 'videobook' || f === 'videobook' ? '🎬 Videolibro' : f.format || f}
                          {f.isbn ? ` (ISBN: ${f.isbn})` : ''}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="audiobook">🎧 Audiolibro</option>
                        <option value="ebook">📱 eBook</option>
                        <option value="printed">📖 Impreso</option>
                        <option value="videobook">🎬 Videolibro</option>
                      </>
                    )}
                  </select>
                </div>
              )}

              {/* Description - Optional when work is selected */}
              <div>
                <label className="block text-white/70 text-sm mb-2">
                  Descripción {!directSale.work_id && '*'}
                </label>
                <input
                  type="text"
                  value={directSale.description}
                  onChange={(e) => setDirectSale({...directSale, description: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                  placeholder={directSale.work_id ? "(Se generará automáticamente del libro)" : "Ej: Venta Amazon Kindle - El Jenga de la Vida"}
                  data-testid="direct-sale-description"
                />
                {directSale.work_id && !directSale.description && (
                  <p className="text-white/50 text-xs mt-1">
                    Si dejas vacío, se usará el nombre del libro y formato seleccionado.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">Plataforma de Venta</label>
                <select
                  value={directSale.platform}
                  onChange={(e) => setDirectSale({...directSale, platform: e.target.value})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                >
                  <option value="Venta Directa">Venta Directa</option>
                  <option value="Amazon">Amazon</option>
                  <option value="Apple Books">Apple Books</option>
                  <option value="Google Play">Google Play</option>
                  <option value="Audible">Audible</option>
                  <option value="Kobo">Kobo</option>
                  <option value="Spotify">Spotify</option>
                  <option value="5BX Publishing & Distribution">5BX Publishing & Distribution</option>
                  <option value="Otra">Otra</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Monto Neto *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={directSale.gross_income}
                    onChange={(e) => setDirectSale({...directSale, gross_income: e.target.value})}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                    placeholder="0.00"
                    data-testid="direct-sale-amount"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Moneda</label>
                  <select
                    value={directSale.currency}
                    onChange={(e) => setDirectSale({...directSale, currency: e.target.value})}
                    className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="MXN">MXN</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-white/70 text-sm mb-2">Unidades Vendidas</label>
                <input
                  type="number"
                  min="1"
                  value={directSale.units_sold}
                  onChange={(e) => setDirectSale({...directSale, units_sold: parseInt(e.target.value) || 1})}
                  className="w-full bg-black/30 border border-purple-500/30 rounded-lg px-4 py-3 text-white"
                />
              </div>

              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                <p className="text-purple-400 text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  El monto ingresado es el neto a pagar al autor (sin descuentos de plataforma/distribuidor).
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <Button 
                onClick={() => {
                  setShowDirectSaleModal(false);
                  setDirectSale({
                    author_id: '',
                    work_id: '',
                    format: '',
                    description: '',
                    platform: 'Venta Directa',
                    units_sold: 1,
                    gross_income: '',
                    currency: 'MXN'
                  });
                  setAuthorWorks([]);
                }} 
                variant="outline" 
                className="flex-1 border-white/20 text-white"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAddDirectSale} 
                disabled={!directSale.author_id || !directSale.gross_income || (directSale.work_id && !directSale.format) || savingDirectSale}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                data-testid="save-direct-sale-btn"
              >
                {savingDirectSale ? 'Guardando...' : 'Registrar Venta'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pool Contribution Modal */}
      {showPoolContributionModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-blue-500/30 rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-white/10">
              <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Aporte al Pool de Autores
              </h2>
              <p className="text-white/60 text-sm mt-1">
                Agregar un monto para distribuir entre los autores activos
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Descripción *</label>
                <input
                  type="text"
                  value={poolContribution.description}
                  onChange={(e) => setPoolContribution({...poolContribution, description: e.target.value})}
                  className="w-full bg-black/30 border border-blue-500/30 rounded-lg px-4 py-3 text-white"
                  placeholder="Ej: Suscripciones Premium Marzo 2026"
                  data-testid="pool-contribution-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Monto a Distribuir *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={poolContribution.amount}
                    onChange={(e) => setPoolContribution({...poolContribution, amount: e.target.value})}
                    className="w-full bg-black/30 border border-blue-500/30 rounded-lg px-4 py-3 text-white"
                    placeholder="0.00"
                    data-testid="pool-contribution-amount"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Moneda</label>
                  <select
                    value={poolContribution.currency}
                    onChange={(e) => setPoolContribution({...poolContribution, currency: e.target.value})}
                    className="w-full bg-black/30 border border-blue-500/30 rounded-lg px-4 py-3 text-white"
                  >
                    <option value="MXN">MXN</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-400 text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Este monto se agregará al Pool de Autores para ser distribuido <strong>automáticamente</strong> entre los autores activos.
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <Button 
                onClick={() => {
                  setShowPoolContributionModal(false);
                  setPoolContribution({ description: '', amount: '', currency: 'MXN' });
                }} 
                variant="outline" 
                className="flex-1 border-white/20 text-white"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAddPoolContribution} 
                disabled={!poolContribution.description || !poolContribution.amount || savingPoolContribution}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="save-pool-contribution-btn"
              >
                {savingPoolContribution ? 'Guardando...' : 'Agregar al Pool'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Webhook Sale Modal (Publica.la) */}
      {showWebhookSaleModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-orange-500/30 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
                <Upload className="w-5 h-5 text-orange-400" />
                Registrar Venta de Webhook
              </h2>
              <p className="text-white/60 text-sm mt-1">
                Registrar manualmente una venta que no llegó por el webhook de Publica.la/App.Viubux
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Mostrar credenciales del autor auto-creado */}
              {autoCreatedAuthor && (
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-4">
                  <h3 className="gotham-bold text-green-400 flex items-center gap-2 mb-3">
                    <UserPlus className="w-5 h-5" />
                    ⚠️ Author Lite CREADO Automáticamente
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-white">
                      <span className="text-white/60">Email:</span>{' '}
                      <code className="bg-black/50 px-2 py-0.5 rounded text-green-300">{autoCreatedAuthor.email}</code>
                    </p>
                    <p className="text-white">
                      <span className="text-white/60">Nombre:</span>{' '}
                      <span className="text-white">{autoCreatedAuthor.full_name}</span>
                    </p>
                    <p className="text-white">
                      <span className="text-white/60">Contraseña temporal:</span>{' '}
                      <code className="bg-black/50 px-2 py-0.5 rounded text-yellow-300 font-mono">{autoCreatedAuthor.temporary_password}</code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(autoCreatedAuthor.temporary_password);
                          toast.success('Contraseña copiada');
                        }}
                        className="ml-2 text-white/50 hover:text-white"
                      >
                        <Copy className="w-4 h-4 inline" />
                      </button>
                    </p>
                    <p className="text-white">
                      <span className="text-white/60">Obra creada:</span>{' '}
                      <span className="text-white">{autoCreatedAuthor.work_created}</span>
                    </p>
                  </div>
                  <div className="mt-3 pt-3 border-t border-green-500/30">
                    <p className="text-yellow-300 text-xs">
                      {autoCreatedAuthor.note}
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      setAutoCreatedAuthor(null);
                      setShowWebhookSaleModal(false);
                    }}
                    className="w-full mt-4 bg-green-600 hover:bg-green-500 text-white"
                  >
                    Entendido, cerrar
                  </Button>
                </div>
              )}

              {!autoCreatedAuthor && (
                <>
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-4">
                    <p className="text-orange-400 text-sm">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Si el autor no existe, se creará un <strong>Author Lite</strong> automáticamente con email @autores.viubux.com
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">
                        Email del Autor
                        <span className="text-white/40 text-xs ml-1">(si ya existe)</span>
                      </label>
                      <input
                        type="email"
                        value={webhookSale.author_email}
                        onChange={(e) => setWebhookSale({...webhookSale, author_email: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                        placeholder="autor@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">
                        Nombre del Autor
                        <span className="text-white/40 text-xs ml-1">(para Author Lite nuevo)</span>
                      </label>
                      <input
                        type="text"
                        value={webhookSale.author_name}
                        onChange={(e) => setWebhookSale({...webhookSale, author_name: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                        placeholder="Juan Pérez García"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/70 text-sm mb-2">ISBN / External ID *</label>
                    <input
                      type="text"
                      value={webhookSale.isbn}
                      onChange={(e) => setWebhookSale({...webhookSale, isbn: e.target.value})}
                      className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                      placeholder="9788410369368"
                    />
                  </div>

                  <div>
                    <label className="block text-white/70 text-sm mb-2">Nombre del Contenido *</label>
                    <input
                      type="text"
                      value={webhookSale.content_name}
                      onChange={(e) => setWebhookSale({...webhookSale, content_name: e.target.value})}
                      className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                      placeholder="Título del libro o audiolibro"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Tipo de Contenido</label>
                      <select
                        value={webhookSale.content_type}
                        onChange={(e) => setWebhookSale({...webhookSale, content_type: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                      >
                        <option value="audio">Audio</option>
                        <option value="ebook">eBook</option>
                        <option value="video">Video</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Plataforma</label>
                      <input
                        type="text"
                        value={webhookSale.platform}
                        onChange={(e) => setWebhookSale({...webhookSale, platform: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                        placeholder="Viubux"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Monto Venta (MXN)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={webhookSale.sale_amount_mxn}
                        onChange={(e) => setWebhookSale({...webhookSale, sale_amount_mxn: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                        placeholder="45.00"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Payout (USD) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={webhookSale.payout_amount_usd}
                        onChange={(e) => setWebhookSale({...webhookSale, payout_amount_usd: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                        placeholder="0.36 (payout_amount_in_cents / 100)"
                      />
                      <p className="text-white/50 text-xs mt-1">Del webhook: payout_amount_in_cents ÷ 100</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Editorial</label>
                      <input
                        type="text"
                        value={webhookSale.publisher}
                        onChange={(e) => setWebhookSale({...webhookSale, publisher: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                        placeholder="Traducine"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Fecha de Venta</label>
                      <input
                        type="date"
                        value={webhookSale.sale_date}
                        onChange={(e) => setWebhookSale({...webhookSale, sale_date: e.target.value})}
                        className="w-full bg-black/30 border border-orange-500/30 rounded-lg px-4 py-3 text-white"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              <Button 
                onClick={() => {
                  setShowWebhookSaleModal(false);
                  setWebhookSale({
                    author_email: '',
                    isbn: '',
                    content_name: '',
                    content_type: 'audio',
                    sale_amount_mxn: '',
                    payout_amount_usd: '',
                    platform: 'Viubux',
                    publisher: '',
                    sale_date: ''
                  });
                }} 
                variant="outline" 
                className="flex-1 border-white/20 text-white"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAddWebhookSale} 
                disabled={!webhookSale.author_email || !webhookSale.isbn || !webhookSale.content_name || !webhookSale.payout_amount_usd || savingWebhookSale}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
              >
                {savingWebhookSale ? 'Registrando...' : 'Registrar Venta'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Coproductores */}
      {showCoproducersModal && selectedWorkForCoprod && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="coproducers-modal">
          <div className="bg-gray-900 border border-blue-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    Gestión de Coproductores
                  </h2>
                  <p className="text-white/60 text-sm mt-1">
                    Obra: <span className="text-white">{selectedWorkForCoprod.title}</span>
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCoproducersModal(false)}
                  className="text-white/50 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Resumen de distribución */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <h3 className="text-blue-400 font-medium mb-3">Distribución Actual</h3>
                <div className="flex gap-4">
                  <div className="flex-1 bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-white/50 text-xs">Autor Principal</p>
                    <p className="text-2xl font-bold text-green-400">
                      {100 - coproducerStats.total_percentage_assigned}%
                    </p>
                  </div>
                  <div className="flex-1 bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-white/50 text-xs">Coproductores</p>
                    <p className="text-2xl font-bold text-blue-400">
                      {coproducerStats.total_percentage_assigned}%
                    </p>
                  </div>
                  <div className="flex-1 bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-white/50 text-xs">Disponible</p>
                    <p className="text-2xl font-bold text-white">
                      {coproducerStats.percentage_available}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Lista de coproductores actuales */}
              <div>
                <h3 className="text-white font-medium mb-3">Coproductores Actuales</h3>
                {loadingCoproducers ? (
                  <div className="text-center py-6">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                  </div>
                ) : coproducers.length === 0 ? (
                  <div className="bg-white/5 rounded-xl p-6 text-center">
                    <Users className="w-10 h-10 text-white/20 mx-auto mb-2" />
                    <p className="text-white/40">No hay coproductores asignados</p>
                    <p className="text-white/30 text-sm">El autor principal recibe el 100% de las regalías</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {coproducers.map((cp) => (
                      <div 
                        key={cp.user_id}
                        className="bg-white/5 rounded-lg p-4 flex items-center justify-between"
                        data-testid={`coproducer-item-${cp.user_id}`}
                      >
                        <div className="flex-1">
                          <p className="text-white font-medium">{cp.full_name || 'Usuario'}</p>
                          <p className="text-white/50 text-sm">{cp.email}</p>
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded mt-1 inline-block">
                            {cp.role}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={editPct[cp.user_id] !== undefined ? editPct[cp.user_id] : cp.percentage}
                              onChange={(e) => setEditPct((prev) => ({ ...prev, [cp.user_id]: e.target.value }))}
                              data-testid={`coproducer-pct-input-${cp.user_id}`}
                              className="w-20 bg-black/30 border border-white/15 rounded-lg px-2 py-1.5 text-white text-lg font-bold text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-blue-400 font-bold">%</span>
                          </div>
                          {editPct[cp.user_id] !== undefined && parseFloat(editPct[cp.user_id]) !== cp.percentage && (
                            <Button
                              onClick={() => handleUpdateCoproducerPercentage(cp.user_id, editPct[cp.user_id])}
                              size="sm"
                              data-testid={`coproducer-save-pct-${cp.user_id}`}
                              className="bg-blue-600 hover:bg-blue-500 text-white h-8 text-xs"
                            >
                              Guardar
                            </Button>
                          )}
                          <Button
                            onClick={() => handleRemoveCoproducer(cp.user_id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Agregar nuevo coproductor */}
              {coproducerStats.percentage_available > 0 && (
                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-white font-medium mb-3">Agregar Coproductor</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                      <label className="block text-white/70 text-sm mb-2">Usuario</label>
                      <select
                        value={newCoproducer.user_id}
                        onChange={(e) => setNewCoproducer({...newCoproducer, user_id: e.target.value})}
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-white"
                        data-testid="select-coproducer-user"
                      >
                        <option value="">Seleccionar...</option>
                        {authors
                          .filter(a => a.id !== selectedWorkForCoprod.author_id && !coproducers.find(cp => cp.user_id === a.id))
                          .map(a => (
                            <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>
                          ))
                        }
                      </select>
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">
                        Porcentaje (máx {coproducerStats.percentage_available}%)
                      </label>
                      <input
                        type="number"
                        min="0.1"
                        max={coproducerStats.percentage_available}
                        step="0.1"
                        value={newCoproducer.percentage}
                        onChange={(e) => setNewCoproducer({...newCoproducer, percentage: e.target.value})}
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-white"
                        placeholder="Ej: 25"
                        data-testid="input-coproducer-percentage"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Rol</label>
                      <select
                        value={newCoproducer.role}
                        onChange={(e) => setNewCoproducer({...newCoproducer, role: e.target.value})}
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-white"
                        data-testid="select-coproducer-role"
                      >
                        <option value="coproductor">Coproductor</option>
                        <option value="narrador">Narrador</option>
                        <option value="editor">Editor</option>
                        <option value="productor">Productor</option>
                        <option value="ilustrador">Ilustrador</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button
                      onClick={handleAddCoproducer}
                      disabled={!newCoproducer.user_id || !newCoproducer.percentage || savingCoproducer}
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                      data-testid="add-coproducer-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {savingCoproducer ? 'Agregando...' : 'Agregar Coproductor'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Información */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-400 text-sm">
                  <strong>Nota:</strong> Las regalías de futuras ventas se distribuirán automáticamente 
                  según los porcentajes configurados. El autor principal recibe el porcentaje restante 
                  ({100 - coproducerStats.total_percentage_assigned}%).
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex justify-end">
              <Button
                onClick={() => setShowCoproducersModal(false)}
                className="bg-white/10 hover:bg-white/20 text-white"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Warning Modal */}
      {showDuplicateModal && duplicateWarning && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="duplicate-warning-modal">
          <div className="bg-gray-900 border border-yellow-500/50 rounded-2xl max-w-lg w-full shadow-2xl">
            <div className="p-6 border-b border-yellow-500/30 bg-yellow-500/10 rounded-t-2xl">
              <h3 className="text-xl font-bold text-yellow-400 flex items-center gap-3">
                <AlertCircle className="w-6 h-6" />
                ⚠️ Archivo Duplicado Detectado
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-white">
                Este archivo ya fue cargado anteriormente en el sistema.
              </p>
              
              {duplicateWarning.originalUpload && (
                <div className="bg-black/40 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Archivo original:</span>
                    <span className="text-white font-medium">{duplicateWarning.originalUpload.filename}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Fecha de carga:</span>
                    <span className="text-white font-medium">
                      {duplicateWarning.originalUpload.uploaded_at 
                        ? new Date(duplicateWarning.originalUpload.uploaded_at).toLocaleString('es-MX')
                        : 'Desconocida'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Cargado por:</span>
                    <span className="text-white font-medium">{duplicateWarning.originalUpload.uploaded_by || 'Desconocido'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Entradas procesadas:</span>
                    <span className="text-white font-medium">{duplicateWarning.originalUpload.entries_count || 0}</span>
                  </div>
                </div>
              )}

              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-400 text-sm">
                  <strong>⚠️ Advertencia:</strong> Cargar el mismo archivo dos veces podría duplicar 
                  las entradas de ventas y causar errores en los reportes de regalías.
                </p>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-400 text-sm">
                  <strong>💡 Nota:</strong> Si deseas cargar nuevas ventas del mismo período, 
                  asegúrate de que el archivo contenga únicamente las transacciones nuevas.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
              <Button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateWarning(null);
                  setImportFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="bg-white/10 hover:bg-white/20 text-white"
                data-testid="cancel-duplicate-upload"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setForceUpload(true);
                  // Proceed with import, system will still check individual transaction duplicates
                  handleImportCSV(true);
                }}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-medium"
                data-testid="force-duplicate-upload"
              >
                ⚠️ Cargar de Todas Formas
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRoyalties;
